import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import MusicOffIcon from "@mui/icons-material/MusicOff";
import PauseIcon from "@mui/icons-material/Pause";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SettingsIcon from "@mui/icons-material/Settings";
import TollIcon from "@mui/icons-material/Toll";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import {
    Alert,
    Box,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Popover,
    Tooltip,
    Typography,
    keyframes,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import debounce from "lodash/debounce";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useConfetti } from "../../../context/ConfettiContext";
import PuzzleAccessGate from "../../../pages/login/PuzzleAccessGate";
import { trackEvent } from "../../../services/GoogleAnalytics";
import AuthService from "../../../services/AuthService";
import UserService from "../../../services/UserService";
import UtilService from "../../../services/UtilService";
import HomeCreditService from "../../../services/HomeCreditService";
import HomePuzzleCoinsService from "../../../services/HomePuzzleCoinsService";
import { resolveTotalPuzzleCoins } from "../../../services/PuzzleCoinsUtil";
import AudioButton from "../../components/AudioButton";
import { resolveBgmAutoPlayPreference } from "../../components/bgmPreference";
import { PUZZLE_BGM_TRACKS } from "../../components/bgmTracks";
import CustomSnackbar from "../../components/CustomSnackbar";
import LoginConfirmDialog from "../../components/LoginConfirmDialog";
import SubscriptionDialog from "../../subscription/SubscriptionDialog";
import CopyrightLine from "../common/CopyrightLine";
import PlayElapsedTimer from "../common/components/PlayElapsedTimer";
// import GamePausedOverlay from "../common/components/GamePausedOverlay";
import usePlayVisibilityPause from "../common/hooks/usePlayVisibilityPause";
import SocialShareDropdown from "../common/components/SocialShareDropdown";
import FeedbackDialog from "../common/feedback/FeedbackDialog";
import FeedbackService from "../common/feedback/FeedbackService";
import HomeService from "../../../services/HomeService";
import {
    calculateProgress,
    findFilledCoordByWord,
    findMatchingCellsAsTyped,
    findMatchingWord,
    findWordInGrid,
    getFoundCellKeySetFromWords,
    normalizeWord,
    parseWordsFound,
    removeHyphens
} from "../common/utils/PuzzlePlayLogic";
import PdfFormatDialog, {
    getPdfDownloadUseDisplayClue,
    PDF_FORMAT_DEFAULT_KEY,
} from "../../../admin/pzl_common/PdfFormatDialog";
import { getLocalizedStoryFromTemplate } from "../../../admin/pzl_common/getEnStoryFromTemplate";
import { UserLayoutContext } from "../../route/UserLayoutContext";
import StoryPlayStoryAccordion from "../../story/StoryPlayStoryAccordion";
import StoryPlayStoryDialog from "../../story/StoryPlayStoryDialog";
import CrossWordCluesPanel from "./components/CrossWordCluesPanel";
import CrosswordPlayProgressAside from "./components/CrosswordPlayProgressAside";
import CrosswordScrambleRack from "./components/CrosswordScrambleRack";
import CrossGrid from "./CrossGrid";
import CrossService from "./CrossService";
import { ACCENT, SITE_BG, SURFACE } from "../../../dark-theme/palette";
import { panelSurfaceSx, playPillButtonSx } from "../../../dark-theme/surfaces";
import WordRevealFx, {
    WORD_REVEAL_BANK_BOUNCE_MS,
    WORD_REVEAL_ILLUMINATE_MS,
    WORD_REVEAL_LETTER_STAGGER_MS,
    measureWordBankRect,
    measureWordCellRects,
} from "../common/components/WordRevealFx";
import {
    buildCrossMeaningsMapsFromDisplayClue,
    lookupCrossWordMeaningPreview,
} from "./crossMeaningPreviewUtils";
import AudioService, { AUDIO_EFFECT } from "../../../services/AudioService";
import Logger from "../../../services/Logger";
import {
    buildPuzzleAccessContext,
    canAccessWordPuzzle,
    isCatalogFreePuzzle,
    isWordPuzzleCreditGated,
    isWordPuzzleOpenAccess,
} from "../common/puzzleTierAccess";
import { usePagesTranslation } from "../../locales";
import {
    buildPuzzleWordMatchSet,
    buildStoryTokenToClueNumberMap,
} from "../../story/storyPlayUtils";
import {
    collectWordObjsForStoryMask,
    MaskedStorySnippet,
} from "../../browse-stories/storyCardMask";
import { resolveStoryShareIdFromPlayPayload } from "../../browse-stories/storyRoUtils";
import { CROSSWORD_HINT_WORD_LIMIT } from "../common/crosswordHintWordLimit";
import { usePageTitle } from "../common/hooks/usePageTitle";
import usePlayRecord from "../common/hooks/usePlayRecord";
import {
    getCrosswordHintTargetWordsNormalized,
    hintRevealSlotCount,
    hintUsedStringFromPlayRo,
    hintUsedStringWithPendingAppends,
    mergePuzzleHintUsedIntoState,
} from "../common/utils/playHintTargets";

const HIDE_ALL_WORDS = true;

/** Build fill-strip string from per-cell draft letters (spaces align indices with `wi.cells`). */
function crosswordSpacedStringFromDraftMap(wi, mergedLetterMap) {
    const maxLen = Math.min(
        removeHyphens(wi.expectedWord).length,
        wi.cells?.length ?? 0
    );
    if (!maxLen || !wi.cells) return "";
    let s = "";
    for (let i = 0; i < maxLen; i++) {
        const k = `${wi.cells[i].row},${wi.cells[i].col}`;
        s += mergedLetterMap[k] ?? " ";
    }
    return s.replace(/\s+$/, "");
}

/** Next slot along word order with no draft letter yet (`mergedLetterMap` keys `"row,col"`). */
function firstEmptySlotIndex(wi, mergedLetterMap, maxLen) {
    for (let i = 0; i < maxLen; i++) {
        const k = `${wi.cells[i].row},${wi.cells[i].col}`;
        if (!mergedLetterMap[k]) return i;
    }
    return -1;
}

/** Last slot along word order that has a draft letter — used for Backspace as “delete last typed”. */
function lastFilledSlotIndex(wi, mergedLetterMap, maxLen) {
    for (let i = maxLen - 1; i >= 0; i--) {
        const k = `${wi.cells[i].row},${wi.cells[i].col}`;
        if (mergedLetterMap[k]) return i;
    }
    return -1;
}

/** True when every slot has a letter (matches fill-strip / spaced encoding). */
function crossWordAllSlotsFilled(rawInput, expectedWord, cellsLength) {
    const maxLen = Math.min(removeHyphens(expectedWord).length, cellsLength);
    if (!maxLen) return false;
    const padded = String(rawInput ?? "").padEnd(maxLen, " ");
    for (let i = 0; i < maxLen; i++) {
        if (!/[a-zA-Z]/i.test(padded[i])) return false;
    }
    return true;
}

/** Localized story title/body/image present — story accordion + story clues column on `/story/play-cross` and `/puzzle/play-cross`. */
function templateHasPlayableStory(template) {
    if (!template) return false;
    const { title, text, imageUrl } = getLocalizedStoryFromTemplate(template);
    const titleOk = typeof title === "string" && title.trim();
    const bodyOk = typeof text === "string" && text.trim();
    const imgOk = typeof imageUrl === "string" && imageUrl.trim();
    return Boolean(titleOk || bodyOk || imgOk);
}

/** Content is under UserLayout header; avoid height:100vh here or the page is taller than the viewport and the header sits off-screen. */
const BELOW_APP_HEADER_MIN = "calc(100vh - 5.5rem)";

/** Left play toolbar: smaller icons than default (~24px) for crossword/grid play headers */
const PLAY_TOOLBAR_ICON_BTN_SIZING_SX = {
    padding: "5px",
    "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
};

/** When the player has hint credits, briefly light up the hint button on this interval. */
const HINT_BUTTON_GLOW_INTERVAL_MS = 3 * 60 * 1000;
const HINT_BUTTON_GLOW_DURATION_MS = 2800;

const hintButtonLightOn = keyframes`
  0%, 100% {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: ${SURFACE.panel};
    color: ${ACCENT.light};
  }
  35% {
    box-shadow: 0 0 0 4px rgba(255, 193, 7, 0.4), 0 0 18px rgba(255, 213, 79, 0.9);
    background-color: #FFF8E1;
    color: #F9A825;
  }
  65% {
    box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.28), 0 0 12px rgba(255, 213, 79, 0.65);
    background-color: #FFECB3;
    color: #FBC02D;
  }
`;

const MIST_PAGE_SX = {
    position: "relative",
    isolation: "isolate",
    width: "100%",
    flex: 1,
    minHeight: "100%",
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
    overflowY: "auto",
    bgcolor: SITE_BG,
};

/** Status lines sit straight on the night backdrop, so they run light. */
const MIST_STATUS_TEXT_SX = { color: "rgba(226,232,240,0.86)" };

const FROSTED_PANEL_SX = {
    ...panelSurfaceSx,
    boxShadow: "0 8px 28px rgba(2, 6, 18, 0.45)",
};

function MistPlayShell({ children, sx: sxProp }) {
    const fitContent = sxProp?.flex === "0 0 auto";
    return (
        <Box sx={{ ...MIST_PAGE_SX, ...sxProp }}>
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flex: fitContent ? "0 0 auto" : 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: fitContent ? "auto" : 0,
                    width: "100%",
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

const CrossPlayMain = ({
    crossTplId: propCrossTplId,
    userSubscription: propUserSubscription,
    onPuzzleTitleChange: propOnPuzzleTitleChange,
    onPuzzleSubtitleChange: propOnPuzzleSubtitleChange,
    onFoundWordsChange,
    onPuzzleTypeChange,
    /** Story-of-day layout: English story title above the grid; collapsible story accordion above the clues column. */
    storyPlayLayout = false,
}) => {
    const { t } = usePagesTranslation();
    const {
        onPuzzleTitleChange: contextOnPuzzleTitleChange,
        onPuzzleSubtitleChange: contextOnPuzzleSubtitleChange,
        hasSiteAdBanner,
    } = useContext(UserLayoutContext);
    const onPuzzleTitleChange = propOnPuzzleTitleChange ?? contextOnPuzzleTitleChange;
    const onPuzzleSubtitleChange = propOnPuzzleSubtitleChange ?? contextOnPuzzleSubtitleChange;
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { triggerConfetti } = useConfetti();
    const playMinHeight = hasSiteAdBanner ? "auto" : BELOW_APP_HEADER_MIN;
    const playShellSx = {
        minHeight: playMinHeight,
        ...(hasSiteAdBanner ? { flex: "0 0 auto" } : {}),
    };

    const puzzleType = "CROSSWORD";
    // Ensure crossTplId is always a string (UUID)
    const crossTplId = propCrossTplId || String(id || '');

    usePlayRecord({
        puzzleType,
        puzzleId: crossTplId || null,
        enabled: Boolean(crossTplId),
    });

    const [wordsFound, setWordsFound] = useState([]);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [subscriptionTriggerReason] = useState('general'); // Used by SubscriptionDialog
    const [userSubscription, setUserSubscription] = useState(propUserSubscription || 'free');
    const [loginDialogOpen, setLoginDialogOpen] = useState(false);
    const [loginDialogFeature, setLoginDialogFeature] = useState('hint'); // 'hint' or 'pdf'
    const [puzzleData, setPuzzleData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [userInfo, setUserInfo] = useState(null);
    const [wordsDrawerOpen, setWordsDrawerOpen] = useState(true); // Start as true, show panel by default
    const [hintsEnabled, setHintsEnabled] = useState(false);
    /** In-flight hint-used lines so slot budget matches server+pending and hint cells do not flicker. */
    const [crossHintUsedPendingWords, setCrossHintUsedPendingWords] = useState([]);
    /** Cached hint cells for CrossGrid (patterns derived per word from cells + hintRevealCells) */
    const hintRevealMetaRef = useRef({ cells: [] });
    /** Paid letter hints: first-letter cells revealed via the toolbar Hint button. */
    const [paidHintRevealCells, setPaidHintRevealCells] = useState([]);
    const [letterHintBusy, setLetterHintBusy] = useState(false);
    const [letterHintConsumedVisible, setLetterHintConsumedVisible] = useState(false);
    const letterHintConsumedTimerRef = useRef(null);
    const [hintButtonGlowing, setHintButtonGlowing] = useState(false);
    /** One PUT /home/cross-play/complete-story per solve when story chrome + full crossword (retries on failure). */
    const crossCompleteStoryApiSentRef = useRef(false);
    const [soundEnabled, setSoundEnabled] = useState(true); // Controls sound effects
    const [bgmAutoPlay, setBgmAutoPlay] = useState(() => resolveBgmAutoPlayPreference(true));
    const [bgmPlaying, setBgmPlaying] = useState(false);
    const [settingsAnchor, setSettingsAnchor] = useState(null);
    const [timerPaused, setTimerPaused] = useState(false);
    const bgmButtonWrapRef = useRef(null);
    const [walletPlayCredits, setWalletPlayCredits] = useState(null);
    const [walletHintCredits, setWalletHintCredits] = useState(null);
    const [walletPuzzleCoins, setWalletPuzzleCoins] = useState(null);
    const [walletBalancesLoading, setWalletBalancesLoading] = useState(false);
    const [isBoldFont, setIsBoldFont] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("error");
    const [confettiTriggered, setConfettiTriggered] = useState(false);
    const [startPlayCalled, setStartPlayCalled] = useState(false);
    const [playHistoryLoaded, setPlayHistoryLoaded] = useState(false); // Track if play history has been loaded
    const [manualValidation, setManualValidation] = useState(false);
    const [settingsLoaded, setSettingsLoaded] = useState(false); // Track if user settings have been loaded
    const [typedWord, setTypedWord] = useState('');
    const [highlightCells, setHighlightCells] = useState([]); // Array of {row, col, charIndex}
    const [selectedWord, setSelectedWord] = useState(null); // Track selected word for meaning display
    const [selectedWords, setSelectedWords] = useState([]); // Track all selected words (words containing clicked cell)
    const [isAuthenticated, setIsAuthenticated] = useState(false); // Track user authentication status
    /** null | pending | ok | insufficient | error — premium word-puzzle credit charge */
    const [premiumCreditStatus, setPremiumCreditStatus] = useState(null);
    const [premiumCreditError, setPremiumCreditError] = useState("");
    const [wordInputs, setWordInputs] = useState({}); // Track word inputs for highlighted empty words {wordKey: {word, cells, direction}}
    /** Meaning preview lines above grid inputs — built only from puzzle `displayClue` (no dictionary API). */
    const [crossMeaningsLookup, setCrossMeaningsLookup] = useState({ wordMap: {}, clueMap: {} });
    const [localWordInputValues, setLocalWordInputValues] = useState({}); // Immediate values for debounced text fields
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false); // Track if PDF is downloading
    const [pdfFormatDialogOpen, setPdfFormatDialogOpen] = useState(false);
    /** Chat with AI (clues panel + POST /home/assistant/help, GET chat-history) */
    const [cluesAiMode, setCluesAiMode] = useState(false);
    const [assistantPrompt, setAssistantPrompt] = useState("");
    const [assistantLoading, setAssistantLoading] = useState(false);
    const [assistantHistoryLoading, setAssistantHistoryLoading] = useState(false);
    /** @type {Array<{ id: string, role: string, content: string, createdAt: number }>} */
    const [assistantMessages, setAssistantMessages] = useState([]);
    const [storyDialogOpen, setStoryDialogOpen] = useState(false);

    /** ADMIN: live tuning of crossword grid clue-number and letter font sizes (px). Mobile matches CrossGrid `(max-width: 768px)`. */
    const [adminGridClueNumberFontPx, setAdminGridClueNumberFontPx] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches ? 9 : 11
    );
    const [adminGridCellFontPx, setAdminGridCellFontPx] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches ? 14 : 18
    );

    // Computed value: drawer should show only after settings loaded AND manualValidation is false
    // Use useMemo to ensure this is computed consistently and appears immediately when conditions are met
    const puzzleGridRef = useRef(null);
    const audioRef = useRef(null);

    const playWordResolvedChime = useCallback(() => {
        if (!soundEnabled) return;
        AudioService.play(AUDIO_EFFECT.WORD_RESOLVED);
    }, [soundEnabled]);

    const [wordRevealFx, setWordRevealFx] = useState(null);
    const [celebrateCellIndexByKey, setCelebrateCellIndexByKey] = useState(null);
    const [bankLockIn, setBankLockIn] = useState(false);
    const wordRevealSeqRef = useRef(0);
    const celebrateClearTimerRef = useRef(null);
    const bankLockInTimerRef = useRef(null);

    const triggerWordRevealCelebration = useCallback((displayWord, cells) => {
        if (!displayWord || !Array.isArray(cells) || cells.length === 0) return;

        const celebrate = {};
        cells.forEach((c, i) => {
            if (c && typeof c.row === "number" && typeof c.col === "number") {
                celebrate[`${c.row},${c.col}`] = i;
            }
        });
        if (Object.keys(celebrate).length === 0) return;

        setCelebrateCellIndexByKey(celebrate);

        if (celebrateClearTimerRef.current) {
            window.clearTimeout(celebrateClearTimerRef.current);
        }
        celebrateClearTimerRef.current = window.setTimeout(() => {
            setCelebrateCellIndexByKey(null);
            celebrateClearTimerRef.current = null;
        }, WORD_REVEAL_ILLUMINATE_MS + cells.length * WORD_REVEAL_LETTER_STAGGER_MS + 80);

        requestAnimationFrame(() => {
            const cellRects = measureWordCellRects(cells);
            if (cellRects.length === 0) {
                setCelebrateCellIndexByKey(null);
                return;
            }
            wordRevealSeqRef.current += 1;
            setWordRevealFx({
                fxId: wordRevealSeqRef.current,
                word: String(displayWord).trim(),
                cellRects,
                bankRect: measureWordBankRect(),
            });
        });
    }, []);

    const handleWordRevealComplete = useCallback((fxId) => {
        setWordRevealFx((prev) => (prev?.fxId === fxId ? null : prev));
    }, []);

    const handleWordRevealBankImpact = useCallback(() => {
        setBankLockIn(true);
        if (bankLockInTimerRef.current) {
            window.clearTimeout(bankLockInTimerRef.current);
        }
        bankLockInTimerRef.current = window.setTimeout(() => {
            setBankLockIn(false);
            bankLockInTimerRef.current = null;
        }, WORD_REVEAL_BANK_BOUNCE_MS);
    }, []);

    useEffect(() => {
        return () => {
            if (celebrateClearTimerRef.current) {
                window.clearTimeout(celebrateClearTimerRef.current);
            }
            if (bankLockInTimerRef.current) {
                window.clearTimeout(bankLockInTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        AudioService.preload(AUDIO_EFFECT.WORD_RESOLVED);
        return () => AudioService.release(AUDIO_EFFECT.WORD_RESOLVED);
    }, []);

    const initialSettingsLoadedRef = useRef(false); // Track if initial settings load is complete
    /** After applying startPlay words to the grid once; reset when crossTplId changes */
    const historyRestoredToGridRef = useRef(false);

    /** Last grid cell clicked — drives Tab pool for Across/Down at intersections */
    const lastClickedGridCellRef = useRef(null);
    /** Active word for grid typing when multiple highlights exist (sticky until invalid); Tab cycles Across/Down. */
    const crosswordKeyboardWordKeyRef = useRef(null);

    useEffect(() => {
        trackEvent('entered', { name: 'CrossPlayMain' });
    }, []);

    useLayoutEffect(() => {
        if (typeof window === "undefined") return;
        if (loading || !puzzleData) return;
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    }, [loading, puzzleData]);

    // Set Open Graph meta for Facebook/social sharing (title, link preview, image)
    const previewBase = process.env.REACT_APP_PREVIEW_IMAGE_BASE;
    const crosswordPreviewImage = puzzleData?.template?.previewImageUrl || puzzleData?.previewImageUrl
        || (crossTplId ? `${previewBase}/crossword/${crossTplId}.png` : '');
    /** Primary label for header share/PDF (puzzle name first; story title only if puzzle title missing). */
    const crosswordPageDisplayTitle = useMemo(() => {
        if (!puzzleData) return '';
        const puzzleT = String(puzzleData.title || "").trim();
        if (puzzleT) return puzzleT;
        if (puzzleData.template) {
            const { title } = getLocalizedStoryFromTemplate(puzzleData.template);
            const storyT = typeof title === "string" ? title.trim() : "";
            if (storyT) return storyT;
        }
        return "";
    }, [puzzleData]);
    usePageTitle(crosswordPageDisplayTitle || "Crossword Puzzle");

    // Clear cached hint cells when puzzle changes
    useEffect(() => {
        hintRevealMetaRef.current = { cells: [] };
    }, [crossTplId]);

    useEffect(() => {
        setAssistantPrompt("");
        setAssistantMessages([]);
        setCluesAiMode(false);
        setStoryDialogOpen(false);
    }, [crossTplId]);

    useEffect(() => {
        const loadPuzzle = async () => {
            if (!crossTplId) {
                Logger.error('No puzzle ID provided');
                setPuzzleData(null);
                setCrossMeaningsLookup({ wordMap: {}, clueMap: {} });
                setLoading(false);
                return;
            }

            try {
                const puzzle = await CrossService.getPuzzleById(crossTplId);
                if (!puzzle) {
                    Logger.error('Puzzle not found for ID:', crossTplId);
                    setPuzzleData(null);
                    setCrossMeaningsLookup({ wordMap: {}, clueMap: {} });
                    setLoading(false);
                    return;
                }

                // Parse words string into array
                let wordsArray = puzzle.words ? puzzle.words.split('\n').filter(word => word.trim()) : [];

                // Meaning hints above grid inputs + Word Clues: only curated `displayClue` from the puzzle payload (no dictionary API).
                const rawDisplayClue =
                    puzzle.displayClue ??
                    puzzle.display_clue ??
                    puzzle.template?.displayClue ??
                    puzzle.template?.display_clue ??
                    null;
                const displayClueArr = Array.isArray(rawDisplayClue) ? rawDisplayClue : [];
                const { wordMap, clueMap } = buildCrossMeaningsMapsFromDisplayClue(displayClueArr);
                setCrossMeaningsLookup({ wordMap, clueMap });

                // Parse filledCoordinates string into array
                let filledCoordinates = [];
                if (puzzle.template?.filledCoordinates) {
                    try {
                        filledCoordinates = JSON.parse(puzzle.template.filledCoordinates);
                    } catch (error) {
                        Logger.error('Error parsing filledCoordinates:', error);
                        filledCoordinates = [];
                    }
                }

                // Create grid from letters string
                let grid = [];
                if (puzzle.letters) {
                    const lines = puzzle.letters.split('\n');
                    const paddedLines = lines.slice(0, 12);
                    while (paddedLines.length < 12) {
                        paddedLines.push('');
                    }

                    grid = paddedLines.map(line => {
                        const chars = line.split('').map(char => char.toUpperCase());
                        while (chars.length < 12) {
                            chars.push(' ');
                        }
                        return chars.slice(0, 12);
                    });
                }

                // Apply filledCoordinates to grid
                filledCoordinates.forEach(coordinate => {
                    coordinate.positions?.forEach(pos => {
                        if (pos.value && grid[pos.row]?.[pos.col] !== undefined) {
                            grid[pos.row][pos.col] = pos.value.toUpperCase();
                        }
                    });
                });

                // Create processed puzzle data
                setPuzzleData({
                    ...puzzle,
                    words: wordsArray,
                    filledCoordinates,
                    grid
                });
                setUserSubscription(isWordPuzzleOpenAccess(puzzle.difficultyLevel) ? 'free' : 'premium');
            } catch (error) {
                Logger.error('Error loading puzzle:', error);
                setPuzzleData(null);
                setCrossMeaningsLookup({ wordMap: {}, clueMap: {} });
            } finally {
                setLoading(false);
            }
        };

        loadPuzzle();
    }, [crossTplId]);

    // Reset all puzzle play state when navigating to a new puzzle
    useEffect(() => {
        setPuzzleData(null);
        setLoading(true);
        setStartPlayCalled(false);
        setPlayHistoryLoaded(false);
        historyRestoredToGridRef.current = false;
        hintRevealMetaRef.current = { cells: [] };
        setWordsFound([]);
        setHighlightCells([]);
        setSelectedWord(null);
        setSelectedWords([]);
        setWordInputs({});
        setCrossMeaningsLookup({ wordMap: {}, clueMap: {} });
        setLocalWordInputValues({});
        setCrossHintUsedPendingWords([]);
        setPaidHintRevealCells([]);
        setLetterHintBusy(false);
        setLetterHintConsumedVisible(false);
        if (letterHintConsumedTimerRef.current) {
            window.clearTimeout(letterHintConsumedTimerRef.current);
            letterHintConsumedTimerRef.current = null;
        }
    }, [crossTplId]);

    // Mark cells as found when play history is loaded and words are restored
    useEffect(() => {
        if (!playHistoryLoaded || !puzzleData || wordsFound.length === 0) {
            return;
        }
        if (historyRestoredToGridRef.current) {
            return;
        }

        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 25;

        const applyMarks = () => {
            if (cancelled) return;
            if (!puzzleGridRef.current) {
                if (attempts < maxAttempts) {
                    attempts += 1;
                    setTimeout(applyMarks, 80);
                }
                return;
            }
            wordsFound.forEach((word) => {
                const cells = findWordInGrid(word, puzzleData.filledCoordinates);
                if (cells?.length > 0) {
                    puzzleGridRef.current.markClickedCellsAsFound(cells);
                }
            });
            historyRestoredToGridRef.current = true;
        };

        const timeoutId = setTimeout(applyMarks, 150);
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [playHistoryLoaded, puzzleData, wordsFound]);

    // Call startPlay to get user's play history when puzzle loads
    useEffect(() => {
        const callStartPlay = async () => {
            // Only call if puzzle data is loaded, crossTplId exists, and we haven't called it yet
            if (!crossTplId || !puzzleData || startPlayCalled) {
                return;
            }

            try {
                // Call startPlay API to get user's play history
                const playHistory = await CrossService.startPlay(crossTplId);

                if (playHistory) {
                    // Restore found words from play history
                    const restoredWords = playHistory.wordsFound
                        ? parseWordsFound(playHistory.wordsFound)
                        : playHistory.foundWords
                            ? parseWordsFound(playHistory.foundWords) // Backward compatibility
                            : Array.isArray(playHistory)
                                ? parseWordsFound(playHistory)
                                : [];

                    if (restoredWords.length > 0) {
                        setWordsFound(restoredWords);
                    }
                    const hu = hintUsedStringFromPlayRo(playHistory);
                    if (hu !== undefined) {
                        setPuzzleData((p) => mergePuzzleHintUsedIntoState(p, hu));
                    }
                }
                setPlayHistoryLoaded(true);

                // Mark as called to prevent multiple calls
                setStartPlayCalled(true);
            } catch (error) {
                Logger.error('Error calling startPlay:', error);
                // Mark as called even on error to prevent retry loops
                setStartPlayCalled(true);
                setPlayHistoryLoaded(true);
            }
        };

        callStartPlay();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [crossTplId, puzzleData]);

    // Handle user info loading
    useEffect(() => {
        const loadUserInfo = async () => {
            if (!crossTplId) return;

            if (!puzzleData) {
                // Don't set settingsLoaded here — wait until we have puzzleData and have checked user access
                return;
            }

            try {
                const userData = await UserService.getUserInfo();
                setUserInfo(userData);

                // Check authentication status
                const authStatus = await AuthService.isAuthenticated();
                setIsAuthenticated(authStatus);

                // Load manual validation setting (defaults to false if no user data)
                const settingsData = userData ? UserService.getSettingsFormData(userData) : { manualValidation: false, soundEnabled: true };
                const manualValidationEnabled = settingsData.manualValidation || false;
                const soundEnabledSetting = settingsData.soundEnabled !== undefined ? settingsData.soundEnabled : true;
                setManualValidation(manualValidationEnabled);
                setSoundEnabled(soundEnabledSetting);
                setBgmAutoPlay(resolveBgmAutoPlayPreference(settingsData.bgmAutoPlay !== false));
                setWordsDrawerOpen(true); // Always show word clues panel at the beginning
                setSettingsLoaded(true);
                initialSettingsLoadedRef.current = true;
            } catch (error) {
                Logger.error('Error loading user info:', error);
                // On error, check authentication status
                try {
                    const authStatus = await AuthService.isAuthenticated();
                    setIsAuthenticated(authStatus);
                } catch (authError) {
                    setIsAuthenticated(false);
                }
                // On error, use defaults
                setManualValidation(false);
                setSoundEnabled(true);
                setBgmAutoPlay(resolveBgmAutoPlayPreference(true));
                setWordsDrawerOpen(true);
                setSettingsLoaded(true);
                initialSettingsLoadedRef.current = true;
            }
        };

        loadUserInfo();
    }, [crossTplId, puzzleData, isMobile]);

    // Open words drawer when manual validation is disabled (on settings load / change)
    // When manual validation is enabled we don't force-close so user can still open via button
    useEffect(() => {
        if (!settingsLoaded) return;
        if (!initialSettingsLoadedRef.current) return;
        if (!manualValidation) {
            setWordsDrawerOpen(true);
        }
    }, [manualValidation, settingsLoaded, isMobile]);

    const loadWalletBalances = useCallback(async () => {
        if (!isAuthenticated) {
            setWalletPlayCredits(null);
            setWalletHintCredits(null);
            setWalletPuzzleCoins(null);
            return;
        }
        setWalletBalancesLoading(true);
        try {
            const [play, hints, info] = await Promise.all([
                HomeCreditService.fetchBalance(HomeCreditService.WEB_APP_CODE).catch(() => null),
                HomeCreditService.fetchBalance(HomeCreditService.WEB_APP_HINTS_CODE).catch(() => null),
                UserService.getUserInfo().catch(() => null),
            ]);
            setWalletPlayCredits(play?.creditBalance ?? 0);
            setWalletHintCredits(hints?.creditBalance ?? 0);
            setWalletPuzzleCoins(resolveTotalPuzzleCoins(info?.cloudUser));
        } catch (e) {
            Logger.warn("CrossPlayMain load wallet:", e);
        } finally {
            setWalletBalancesLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!settingsAnchor || !isAuthenticated) return;
        void loadWalletBalances();
    }, [settingsAnchor, isAuthenticated, loadWalletBalances]);

    useEffect(() => {
        if (!isAuthenticated) return;
        void loadWalletBalances();
    }, [isAuthenticated, loadWalletBalances]);

    const hasHintCoins = Number(walletPuzzleCoins) > 0;

    const solvedWordsCount = Array.isArray(wordsFound) ? wordsFound.length : 0;
    const isCrosswordComplete =
        Boolean(puzzleData?.words?.length) &&
        calculateProgress(solvedWordsCount, puzzleData.words.length) === 100;
    /** PDF toolbar control is available as soon as the puzzle payload is loaded. */
    const canDownloadPdf = Boolean(puzzleData);

    const { visibilityPaused, continuePlaying } = usePlayVisibilityPause({
        enabled: Boolean(puzzleData) && !loading && !isCrosswordComplete,
    });
    const timerRunning = !isCrosswordComplete && !timerPaused && !visibilityPaused;

    useEffect(() => {
        if (!hasHintCoins || isCrosswordComplete) {
            setHintButtonGlowing(false);
            return undefined;
        }

        let glowOffTimer = null;
        const startGlow = () => {
            setHintButtonGlowing(true);
            if (glowOffTimer) window.clearTimeout(glowOffTimer);
            glowOffTimer = window.setTimeout(() => {
                setHintButtonGlowing(false);
                glowOffTimer = null;
            }, HINT_BUTTON_GLOW_DURATION_MS);
        };

        startGlow();
        const intervalId = window.setInterval(startGlow, HINT_BUTTON_GLOW_INTERVAL_MS);
        return () => {
            window.clearInterval(intervalId);
            if (glowOffTimer) window.clearTimeout(glowOffTimer);
        };
    }, [hasHintCoins, isCrosswordComplete]);

    const persistPlaySetting = useCallback(async (patch) => {
        try {
            const info = await UserService.getUserInfo();
            const id = userInfo?.cloudUser?.id || info?.cloudUser?.id;
            if (!id) return;
            await UserService.updateUserProfile(id, {
                ...UserService.getSettingsFormData(info),
                ...patch,
            });
        } catch (e) {
            Logger.warn("CrossPlayMain save setting:", e);
        }
    }, [userInfo?.cloudUser?.id]);

    const handleToggleSoundEffects = useCallback(() => {
        setSoundEnabled((prev) => {
            const next = !prev;
            void persistPlaySetting({ soundEnabled: next });
            return next;
        });
    }, [persistPlaySetting]);

    // Header: large line = puzzle title; small line = story title (set later when storyChromeLayout + storyPresentation).
    useEffect(() => {
        if (onPuzzleTitleChange && puzzleData) {
            onPuzzleTitleChange(crosswordPageDisplayTitle);
        }
    }, [puzzleData, onPuzzleTitleChange, crosswordPageDisplayTitle]);

    useEffect(() => {
        if (onFoundWordsChange) {
            onFoundWordsChange(wordsFound);
        }
    }, [wordsFound, onFoundWordsChange]);

    useEffect(() => {
        if (onPuzzleTypeChange) {
            onPuzzleTypeChange(puzzleType);
        }
    }, [puzzleType, onPuzzleTypeChange]);

    // Cleanup: Reset cursor if component unmounts during download
    useEffect(() => {
        return () => {
            if (isPdfDownloading) {
                document.body.style.cursor = 'default';
            }
        };
    }, [isPdfDownloading]);

    // Confetti when all words are found
    useEffect(() => {
        if (!puzzleData?.words) return;

        const progressPercentage = calculateProgress(wordsFound.length, puzzleData.words.length);

        if (progressPercentage === 100 && puzzleData.words.length > 0) {
            if (!confettiTriggered) {
                triggerConfetti(10 * 1000); // Trigger confetti for 10 seconds

                if (soundEnabled && audioRef.current) {
                    audioRef.current.play().catch(error => {
                        Logger.log('Audio play failed:', error);
                    });
                }
                setConfettiTriggered(true);
            }
        }
    }, [wordsFound, puzzleData, triggerConfetti, confettiTriggered, soundEnabled]);

    const handlePdf = async () => {
        if (!canDownloadPdf) return;
        const savedFormat = userInfo?.cloudUser?.details?.pdfFormat
            || (typeof window !== "undefined" ? window.localStorage.getItem(PDF_FORMAT_DEFAULT_KEY) : null);
        if (savedFormat === "letter" || savedFormat === "a4") {
            await handlePdfFormatConfirm(savedFormat);
        } else {
            setPdfFormatDialogOpen(true);
        }
    };

    const handlePdfFormatConfirm = async (format) => {
        const pdfUrl = format === 'a4' ? puzzleData?.pdfA4Url : puzzleData?.pdfLetterUrl;
        const templateId = puzzleData?.templateId ?? puzzleData?.template?.id ?? crossTplId;
        setIsPdfDownloading(true);
        document.body.style.cursor = 'wait';
        try {
            await CrossService.recordAndDownloadPdf(templateId, format, {
                pdfUrl: pdfUrl || undefined,
                title: crosswordPageDisplayTitle || puzzleData?.title || 'puzzle',
                useDisplayClue: getPdfDownloadUseDisplayClue(),
            });
        } catch (error) {
            Logger.error('PDF generation error:', error.message);
            setSnackbarMessage(t("puzzle.playCross.pdf-error"));
            setSnackbarOpen(true);
        } finally {
            setIsPdfDownloading(false);
            document.body.style.cursor = 'default';
        }
    };

    const handleBackClick = () => {
        const fromLibrary = location.state?.fromLibrary === true;

        if (fromLibrary) {
            navigate('/account/library', {
                state: {
                    activeTab: location.state?.activeTab ?? 0
                }
            });
            return;
        }

        if (storyPlayLayout) {
            navigate(-1);
            return;
        }

        navigate('/puzzles/crossword');
    };

    const handleSignUpClick = async () => {
        try {
            await AuthService.signup();
        } catch (error) {
            Logger.error("Error redirecting to signup:", error);
        }
    };

    const handleClearClickedWords = () => {
        // Only clear current selection/highlighted cells, not found words
        puzzleGridRef.current?.clearSelection();

        // Clear typed word and highlights if manual validation is enabled
        if (manualValidation) {
            setTypedWord('');
            setHighlightCells([]);
            setWordInputs({});
        }
    };

    // Get the first character position of a word
    const getFirstPosition = (coord) => {
        if (!coord.positions || coord.positions.length === 0) return null;

        // Exclude hidden message words
        if (coord.direction === "scattered") {
            return null;
        }

        let firstPos;

        if (coord.reversed) {
            // Reversed word: first character of word text is at the last position in positions array
            firstPos = coord.positions[coord.positions.length - 1];
        } else {
            // Normal word: get first position based on direction
            if (coord.direction === "vertical") {
                firstPos = coord.positions.reduce((min, pos) => pos.row < min.row ? pos : min);
            } else if (coord.direction === "horizontal") {
                firstPos = coord.positions.reduce((min, pos) => pos.col < min.col ? pos : min);
            } else {
                // For diagonal directions, use the first position in the array
                firstPos = coord.positions[0];
            }
        }

        return firstPos;
    };

    // Calculate word numbers: assign numbers to words based on their starting positions
    const wordNumbers = useMemo(() => {
        if (!puzzleData?.filledCoordinates || puzzleData.filledCoordinates.length === 0) {
            return new Map(); // Map of "row,col" -> number
        }

        // Collect all unique starting positions
        const startingPositions = new Map(); // Map of "row,col" -> {row, col}

        puzzleData.filledCoordinates.forEach(coord => {
            // Skip hidden message words
            if (coord.direction === "scattered") {
                return;
            }

            const firstPos = getFirstPosition(coord);
            if (!firstPos) return;

            const cellKey = `${firstPos.row},${firstPos.col}`;

            // Store the position if not already stored
            if (!startingPositions.has(cellKey)) {
                startingPositions.set(cellKey, { row: firstPos.row, col: firstPos.col });
            }
        });

        // Sort positions: top-to-bottom (by row), then left-to-right (by column)
        const sortedPositions = Array.from(startingPositions.values()).sort((a, b) => {
            if (a.row !== b.row) {
                return a.row - b.row; // Top to bottom
            }
            return a.col - b.col; // Left to right
        });

        // Assign numbers to sorted positions
        const cellToNumber = new Map();
        sortedPositions.forEach((pos, index) => {
            const cellKey = `${pos.row},${pos.col}`;
            cellToNumber.set(cellKey, index + 1); // Start from 1
        });

        return cellToNumber;
    }, [puzzleData?.filledCoordinates]);

    /** Story accordion above full clues list; true on `/story/play-cross` or `/puzzle/play-cross` when template has playable story. */
    const storyChromeLayout =
        storyPlayLayout || templateHasPlayableStory(puzzleData?.template);

    // Access: Easy → anyone; catalog-free puzzles → every difficulty; otherwise Medium/Hard need sign-in.
    const wordDifficulty =
        puzzleData?.difficultyLevel
        ?? puzzleData?.template?.difficultyLevel
        ?? null;
    const markedFree = isCatalogFreePuzzle(puzzleData);
    const isPuzzleFree = markedFree || isWordPuzzleOpenAccess(wordDifficulty);
    const accessContext = useMemo(() => buildPuzzleAccessContext(userInfo), [userInfo]);
    const canAccessPuzzle = useMemo(
        () => canAccessWordPuzzle(wordDifficulty, accessContext, { isFreePuzzle: markedFree }),
        [wordDifficulty, accessContext, markedFree],
    );
    /** ADMIN-only: show expected answers in word fill panel */
    const isAdminRole = userInfo?.cloudUser?.userRole?.toUpperCase() === 'ADMIN';
    /** Puzzle Assistant (Chat with AI): anyone logged in who can play this puzzle */
    const canUseAssistant = isAuthenticated && canAccessPuzzle;
    /** Show magic icon for guests who can play (Easy) too; opens gated chat until sign-in */
    const showAssistantMagic = canUseAssistant || (!isAuthenticated && canAccessPuzzle);

    const needsPremiumCreditCharge = useMemo(() => {
        if (!crossTplId || !puzzleData || !canAccessPuzzle || markedFree) return false;
        if (accessContext.isStaff) return false;
        return isWordPuzzleCreditGated(wordDifficulty);
    }, [crossTplId, puzzleData, canAccessPuzzle, accessContext.isStaff, wordDifficulty, markedFree]);

    useEffect(() => {
        let cancelled = false;

        async function chargePremiumCredit() {
            if (!needsPremiumCreditCharge || !crossTplId) {
                setPremiumCreditStatus(needsPremiumCreditCharge ? null : "ok");
                setPremiumCreditError("");
                return;
            }
            setPremiumCreditStatus("pending");
            setPremiumCreditError("");
            try {
                const result = await HomeCreditService.consumePremiumPlay(crossTplId);
                if (cancelled) return;
                setPremiumCreditStatus("ok");
                if (result.creditsConsumed > 0) {
                    trackEvent("word_puzzle_premium_credit_consumed", {
                        puzzle_type: "crossword",
                        puzzle_id: crossTplId,
                        credits_consumed: result.creditsConsumed,
                        credit_balance: result.creditBalance,
                    });
                }
            } catch (err) {
                if (cancelled) return;
                const message = err?.message || "";
                Logger.warn("Crossword premium credit consume failed:", message);
                if (/insufficient/i.test(message)) {
                    setPremiumCreditStatus("insufficient");
                    setSubscriptionModalOpen(true);
                } else {
                    setPremiumCreditStatus("error");
                    setPremiumCreditError(message);
                }
            }
        }

        chargePremiumCredit();
        return () => {
            cancelled = true;
        };
    }, [needsPremiumCreditCharge, crossTplId]);

    const loadAssistantChatHistory = useCallback(async () => {
        if (!isAuthenticated || !crossTplId) return;
        setAssistantHistoryLoading(true);
        try {
            const data = await HomeService.getAssistantChatHistory({
                page: 0,
                pageSize: 50,
                crosswordId: crossTplId,
            });
            setAssistantMessages(Array.isArray(data?.content) ? data.content : []);
        } catch (err) {
            Logger.error("Assistant chat history:", err);
            setAssistantMessages([]);
        } finally {
            setAssistantHistoryLoading(false);
        }
    }, [isAuthenticated, crossTplId]);

    useEffect(() => {
        if (!canUseAssistant || !cluesAiMode || !crossTplId) return;
        loadAssistantChatHistory();
    }, [canUseAssistant, cluesAiMode, crossTplId, loadAssistantChatHistory]);

    const handleAssistantSubmit = useCallback(async () => {
        if (!canUseAssistant) return;
        const creditsTotal = userInfo?.cloudUser?.assistantCreditsTotal;
        if (typeof creditsTotal === "number" && creditsTotal <= 0) {
            setSnackbarMessage(
                t(
                    "puzzle.playCross.assistant-credits-empty",
                    "You're out of Coins. Add a subscription or coin pack to keep using the assistant."
                )
            );
            setSnackbarSeverity("warning");
            setSnackbarOpen(true);
            return;
        }
        const text = assistantPrompt.trim();
        if (!text || !crossTplId) {
            setSnackbarMessage(t("puzzle.playCross.assistant-empty", "Enter a message for the assistant."));
            setSnackbarSeverity("warning");
            setSnackbarOpen(true);
            return;
        }
        setAssistantLoading(true);
        try {
            await HomeService.postAssistantHelp({ message: text, crosswordId: crossTplId });
            setAssistantPrompt("");
            await loadAssistantChatHistory();
        } catch (err) {
            const msg = err?.message || "Assistant request failed";
            setSnackbarMessage(msg);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            Logger.error("Assistant help error:", err);
        } finally {
            setAssistantLoading(false);
        }
    }, [canUseAssistant, assistantPrompt, crossTplId, t, loadAssistantChatHistory, userInfo?.cloudUser?.assistantCreditsTotal]);

    const isPaidPuzzleNeedingUserCheck = puzzleData && !isPuzzleFree;
    // Guest on Medium/Hard: wait for auth check. Credit paywall is separate.
    const showAccessCheck = isPaidPuzzleNeedingUserCheck && !isAuthenticated;

    /** Localized story title/body/image + word markers for the right column (both play routes when template has story). */
    const storyPresentation = useMemo(() => {
        if (!puzzleData?.template) {
            return {
                titleAboveGrid: "",
                bodyText: "",
                imageUrl: "",
                matchSet: new Set(),
                tokenToClueNumber: new Map(),
                storyId: null,
                showSlidingPuzzle: false,
                showJigsawPuzzle: false,
            };
        }
        const { title, text, imageUrl } = getLocalizedStoryFromTemplate(puzzleData.template);
        const titleAboveGrid = title || "";
        const bodyText = typeof text === "string" ? text : "";
        const imageUrlStr = typeof imageUrl === "string" ? imageUrl : "";
        const coords = puzzleData.filledCoordinates || [];
        const matchSet = buildPuzzleWordMatchSet(coords);
        const tokenToClueNumber = buildStoryTokenToClueNumberMap(coords);
        const storyObj = puzzleData.template.story;
        const storyId = storyObj?.id ? String(storyObj.id).trim() : null;
        const showSlidingPuzzle = Boolean(storyId && storyObj?.sliding?.state === "PUBLISHED");
        const showJigsawPuzzle = Boolean(storyId && storyObj?.jigsaw?.state === "PUBLISHED");
        return { titleAboveGrid, bodyText, imageUrl: imageUrlStr, matchSet, tokenToClueNumber, storyId, showSlidingPuzzle, showJigsawPuzzle };
    }, [puzzleData]);

    const hasPlayStoryContent = Boolean(
        storyChromeLayout &&
            (String(storyPresentation.titleAboveGrid || "").trim() ||
                String(storyPresentation.imageUrl || "").trim() ||
                Boolean(storyPresentation.bodyText && String(storyPresentation.bodyText).trim()))
    );

    useEffect(() => {
        crossCompleteStoryApiSentRef.current = false;
    }, [crossTplId]);

    useEffect(() => {
        if (!isCrosswordComplete) {
            crossCompleteStoryApiSentRef.current = false;
        }
    }, [isCrosswordComplete]);

    useEffect(() => {
        if (!hasPlayStoryContent || !isCrosswordComplete) return;
        if (!crossTplId || !userInfo?.cloudUser?.id) return;
        if (crossCompleteStoryApiSentRef.current) return;
        crossCompleteStoryApiSentRef.current = true;
        CrossService.completeStory(crossTplId, true).catch((err) => {
            Logger.error("Cross complete-story:", err);
            crossCompleteStoryApiSentRef.current = false;
        });
    }, [hasPlayStoryContent, isCrosswordComplete, crossTplId, userInfo?.cloudUser?.id]);

    const storyShareIdFromPayload = useMemo(
        () => resolveStoryShareIdFromPlayPayload(puzzleData),
        [puzzleData]
    );
    const storyShareTitleForShare = useMemo(() => {
        const st = String(storyPresentation.titleAboveGrid || "").trim();
        if (st) return st;
        return String(crosswordPageDisplayTitle || "").trim();
    }, [storyPresentation.titleAboveGrid, crosswordPageDisplayTitle]);

    /** Story title under puzzle title in header — only on `/story/play-cross`; puzzle route keeps a single header line. */
    useEffect(() => {
        if (!onPuzzleSubtitleChange || !puzzleData) return;
        if (!storyPlayLayout || !storyChromeLayout) {
            onPuzzleSubtitleChange(null);
            return;
        }
        const storyT = (storyPresentation.titleAboveGrid || "").trim();
        const puzzleT = (puzzleData.title || "").trim();
        if (puzzleT && storyT && storyT !== puzzleT) {
            onPuzzleSubtitleChange(storyT);
        } else {
            onPuzzleSubtitleChange(null);
        }
    }, [
        puzzleData,
        storyPlayLayout,
        storyChromeLayout,
        onPuzzleSubtitleChange,
        storyPresentation.titleAboveGrid,
    ]);

    /** Feedback / Complete after solve: only for logged-in users (guests see disabled controls). */
    const completionActionsDisabled = !settingsLoaded || !isAuthenticated;

    const wordNetworkData = useMemo(
        () => puzzleData?.template?.wordNetwork || puzzleData?.wordNetwork || null,
        [puzzleData]
    );
    const hasWordNetwork = useMemo(() => {
        const w = puzzleData?.template?.wordNetwork || puzzleData?.wordNetwork;
        return Boolean(w?.nodes?.length && w?.links?.length);
    }, [puzzleData]);

    /** New hint letters this session: env cap minus server + pending `hintUsed` line count. */
    const crossHintSlotBudget = useMemo(() => {
        const huStr = puzzleData?.template?.hintUsed ?? puzzleData?.hintUsed;
        const effective = hintUsedStringWithPendingAppends(huStr, crossHintUsedPendingWords);
        return hintRevealSlotCount(effective, CROSSWORD_HINT_WORD_LIMIT);
    }, [puzzleData?.template?.hintUsed, puzzleData?.hintUsed, crossHintUsedPendingWords]);

    useEffect(() => {
        hintRevealMetaRef.current = { cells: [] };
    }, [crossHintSlotBudget]);

    // Hint cells: unresolved words × remaining hint slots (env cap − server `hintUsed` line count).
    // Cached in ref so toggling hint mode doesn’t reshuffle letters; cache clears when `hintUsed` updates.
    const hintRevealCells = useMemo(() => {
        if (!hintsEnabled) return [];
        const meta = hintRevealMetaRef.current;
        if (meta.cells?.length > 0) {
            return [...meta.cells];
        }
        if (!puzzleData?.filledCoordinates?.length) return [];
        const cells = [];
        const coords = puzzleData.filledCoordinates.filter(
            (c) => (c.text || c.word) && c.positions && c.positions.length > 0 && c.direction !== "scattered"
        );
        const unresolved = coords.filter((c) => {
            const word = normalizeWord(c.text || c.word || "");
            return word && !wordsFound.includes(word);
        });
        const selected = unresolved.slice(0, crossHintSlotBudget);
        selected.forEach((coord) => {
            const positions = coord.positions;
            const len = positions.length;
            if (len === 0) return;
            let idx;
            if (len <= 2) {
                idx = 0;
            } else {
                idx = 1 + Math.floor(Math.random() * (len - 2));
            }
            const pos = positions[idx];
            if (pos && typeof pos.row === "number" && typeof pos.col === "number") {
                cells.push({ row: pos.row, col: pos.col });
            }
        });
        hintRevealMetaRef.current = { cells };
        return cells;
    }, [hintsEnabled, puzzleData?.filledCoordinates, wordsFound, crossHintSlotBudget]);

    const displayHintRevealCells = useMemo(() => {
        const map = new Map();
        const push = (list) => {
            (Array.isArray(list) ? list : []).forEach((c) => {
                if (c && typeof c.row === "number" && typeof c.col === "number") {
                    map.set(`${c.row},${c.col}`, { row: c.row, col: c.col });
                }
            });
        };
        if (hintsEnabled) push(hintRevealCells);
        push(paidHintRevealCells);
        return [...map.values()];
    }, [hintsEnabled, hintRevealCells, paidHintRevealCells]);

    const handleLetterHintClick = useCallback(async () => {
        if (isCrosswordComplete || letterHintBusy) return;

        const cell = lastClickedGridCellRef.current;
        if (!cell || typeof cell.row !== "number" || typeof cell.col !== "number") {
            setSnackbarMessage(t("puzzle.playCross.letter-hint-need-cell"));
            setSnackbarSeverity("info");
            setSnackbarOpen(true);
            return;
        }

        if (!isAuthenticated) {
            setLoginDialogFeature("hint");
            setLoginDialogOpen(true);
            return;
        }

        const wordsContainingCell =
            puzzleData?.filledCoordinates?.filter((coord) => {
                if (!(coord.text || coord.word)) return false;
                if (!coord.positions?.length) return false;
                if (coord.direction === "scattered") return false;
                const nw = normalizeWord(coord.text || coord.word || "");
                if (!nw || wordsFound.includes(nw)) return false;
                return coord.positions.some(
                    (pos) => pos.row === cell.row && pos.col === cell.col,
                );
            }) ?? [];

        if (wordsContainingCell.length === 0) {
            setSnackbarMessage(t("puzzle.playCross.letter-hint-no-word"));
            setSnackbarSeverity("info");
            setSnackbarOpen(true);
            return;
        }

        const active =
            wordsContainingCell.find((c) => c.direction === "horizontal") ?? wordsContainingCell[0];
        const firstPos = active?.positions?.[0];
        if (!firstPos || typeof firstPos.row !== "number" || typeof firstPos.col !== "number") {
            return;
        }

        const alreadyShown = paidHintRevealCells.some(
            (c) => c.row === firstPos.row && c.col === firstPos.col,
        );
        if (alreadyShown) {
            setSnackbarMessage(t("puzzle.playCross.letter-hint-already"));
            setSnackbarSeverity("info");
            setSnackbarOpen(true);
            return;
        }

        setLetterHintBusy(true);
        try {
            const spend = await HomePuzzleCoinsService.spendForHint({
                featureUsed: "crossword_letter_hint",
                coins: 1,
            });
            if (typeof spend?.totalPuzzleCoins === "number") {
                setWalletPuzzleCoins(spend.totalPuzzleCoins);
            } else {
                void loadWalletBalances();
            }

            setPaidHintRevealCells((prev) => [
                ...prev,
                { row: firstPos.row, col: firstPos.col },
            ]);

            setLetterHintConsumedVisible(true);
            if (letterHintConsumedTimerRef.current) {
                window.clearTimeout(letterHintConsumedTimerRef.current);
            }
            letterHintConsumedTimerRef.current = window.setTimeout(() => {
                setLetterHintConsumedVisible(false);
                letterHintConsumedTimerRef.current = null;
            }, 4000);

            const wordLabel = String(active.text || active.word || "").trim();
            if (wordLabel && userInfo?.cloudUser?.id) {
                setCrossHintUsedPendingWords((prev) => [...prev, wordLabel]);
                CrossService.hintUsed(crossTplId, { word: wordLabel })
                    .then((ro) => {
                        const hu = hintUsedStringFromPlayRo(ro);
                        if (hu !== undefined) {
                            setPuzzleData((p) => mergePuzzleHintUsedIntoState(p, hu));
                        }
                        setCrossHintUsedPendingWords((prev) => {
                            const i = prev.indexOf(wordLabel);
                            if (i === -1) return prev;
                            return [...prev.slice(0, i), ...prev.slice(i + 1)];
                        });
                    })
                    .catch((err) => {
                        setCrossHintUsedPendingWords((prev) => {
                            const i = prev.indexOf(wordLabel);
                            if (i === -1) return prev;
                            return [...prev.slice(0, i), ...prev.slice(i + 1)];
                        });
                        Logger.error("Error recording letter hint use:", err);
                    });
            }

            trackEvent("word_puzzle_letter_hint_consumed", {
                puzzle_type: "crossword",
                puzzle_id: crossTplId,
                coins_consumed: spend?.coinsSpent ?? 1,
            });
        } catch (err) {
            const message = err?.message || "";
            Logger.warn("Crossword letter hint coin spend failed:", message);
            if (/insufficient/i.test(message)) {
                setSnackbarMessage(t("puzzle.playCross.letter-hint-insufficient", "Not enough coins."));
                setSnackbarSeverity("warning");
                setSnackbarOpen(true);
                setSubscriptionModalOpen(true);
            } else {
                setSnackbarMessage(t("puzzle.playCross.letter-hint-failed"));
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            }
        } finally {
            setLetterHintBusy(false);
        }
    }, [
        isCrosswordComplete,
        letterHintBusy,
        isAuthenticated,
        puzzleData?.filledCoordinates,
        wordsFound,
        paidHintRevealCells,
        userInfo?.cloudUser?.id,
        crossTplId,
        loadWalletBalances,
        t,
    ]);

    const recordCrossPlayHintUsedIfApplicable = useCallback(
        (wordToAddNormalized, wordsFoundBeforeSolve, solvedWordForApi) => {
            if (!hintsEnabled || !userInfo?.cloudUser?.id || !puzzleData?.filledCoordinates) return;
            const huStr = puzzleData?.template?.hintUsed ?? puzzleData?.hintUsed;
            const effectiveBeforeThis = hintUsedStringWithPendingAppends(huStr, crossHintUsedPendingWords);
            const slots = hintRevealSlotCount(effectiveBeforeThis, CROSSWORD_HINT_WORD_LIMIT);
            const targets = getCrosswordHintTargetWordsNormalized(
                puzzleData.filledCoordinates,
                wordsFoundBeforeSolve,
                slots
            );
            if (!targets.has(wordToAddNormalized)) return;
            const word =
                solvedWordForApi != null && String(solvedWordForApi).trim()
                    ? String(solvedWordForApi).trim()
                    : wordToAddNormalized;
            setCrossHintUsedPendingWords((prev) => [...prev, word]);
            CrossService.hintUsed(crossTplId, { word })
                .then((ro) => {
                    const hu = hintUsedStringFromPlayRo(ro);
                    if (hu !== undefined) {
                        setPuzzleData((p) => mergePuzzleHintUsedIntoState(p, hu));
                    }
                    setCrossHintUsedPendingWords((prev) => {
                        const i = prev.indexOf(word);
                        if (i === -1) return prev;
                        return [...prev.slice(0, i), ...prev.slice(i + 1)];
                    });
                })
                .catch((err) => {
                    setCrossHintUsedPendingWords((prev) => {
                        const i = prev.indexOf(word);
                        if (i === -1) return prev;
                        return [...prev.slice(0, i), ...prev.slice(i + 1)];
                    });
                    Logger.error("Error recording hint use:", err);
                });
        },
        [
            hintsEnabled,
            userInfo?.cloudUser?.id,
            puzzleData?.filledCoordinates,
            puzzleData?.template?.hintUsed,
            puzzleData?.hintUsed,
            crossHintUsedPendingWords,
            crossTplId,
        ]
    );

    const foundCellKeySet = useMemo(
        () => getFoundCellKeySetFromWords(puzzleData?.filledCoordinates, wordsFound),
        [puzzleData?.filledCoordinates, wordsFound]
    );

    // Raw tags (lowercase) for meaning matching - when puzzle has "music", show definitions containing (music)
    const tagsForMeaning = useMemo(() => {
        if (!puzzleData) return [];
        const genreTag = puzzleData.topic?.genre?.name ?? puzzleData.template?.topic?.genre?.name;
        const rawTags = puzzleData.template?.tags ?? puzzleData.template?.topic?.tags ?? puzzleData.tags ?? (genreTag ? [genreTag] : []);
        return (Array.isArray(rawTags) ? rawTags : [])
            .filter((t) => t && String(t).toLowerCase() !== 'bedrock')
            .map((t) => String(t).toLowerCase().trim());
    }, [puzzleData]);

    // Find words from highlighted cells that have empty cells
    const updateWordInputsForHighlightedCells = useCallback((highlightedCells) => {
        if (!puzzleData?.filledCoordinates || !highlightedCells || highlightedCells.length === 0) {
            setWordInputs({});
            return;
        }

        const newWordInputs = {};

        // Check each word in filledCoordinates
        puzzleData.filledCoordinates.forEach(coord => {
            if (!coord.text || !coord.positions || coord.positions.length === 0) return;

            // Check if this word is already found
            const normalizedWord = normalizeWord(coord.text);
            if (wordsFound.includes(normalizedWord)) {
                return; // Skip already found words
            }

            // Check if all positions of this word are in highlighted cells
            const wordCells = coord.positions.map(pos => ({ row: pos.row, col: pos.col }));
            const allCellsHighlighted = wordCells.every(cell =>
                highlightedCells.some(hc => hc.row === cell.row && hc.col === cell.col)
            );

            if (!allCellsHighlighted) return;

            // For crossword puzzles with HIDE_ALL_WORDS, show input if word is not found
            // Check if word cells are not marked as found (i.e., word hasn't been solved yet)
            const hasUnfoundCells = wordCells.some(() => {
                // Check if this cell is in foundWordCells (from the grid ref)
                // Since we can't directly access foundWordCells, we'll show input for all unfound words
                // that are highlighted
                return true; // Show input for any highlighted unfound word
            });

            if (!hasUnfoundCells) return;

            // Determine direction
            const direction = coord.direction ||
                (wordCells.length > 1 && wordCells[0].row === wordCells[1].row ? 'horizontal' : 'vertical');

            // Create a unique key for this word
            const wordKey = `${coord.text}-${direction}-${wordCells[0].row}-${wordCells[0].col}`;

            // Get word number (inline so useCallback deps stay stable)
            const firstPos = getFirstPosition(coord);
            const wordNumber = firstPos
                ? wordNumbers.get(`${firstPos.row},${firstPos.col}`) || null
                : null;
            const coordDisplay =
                coord.word != null && String(coord.word).trim() !== '' ? String(coord.word) : '';
            const meaningPreview = lookupCrossWordMeaningPreview(
                crossMeaningsLookup.wordMap,
                crossMeaningsLookup.clueMap,
                coord.text,
                coordDisplay,
                wordNumber,
                direction
            );

            newWordInputs[wordKey] = {
                word: coord.text,
                cells: wordCells,
                direction: direction,
                expectedWord: coord.text,
                inputValue: '',
                number: wordNumber,
                meaningPreview
            };
        });

        // Logger.log('Word inputs detected:', newWordInputs); // Debug log
        setWordInputs(newWordInputs);
    }, [puzzleData?.filledCoordinates, wordsFound, crossMeaningsLookup, wordNumbers]);

    const handleWordSelectForPlay = useCallback(
        (word) => {
            setSelectedWord(word);
            // Highlight the word on the grid (same as CrossWordCluesPanel row tap)
            if (word && puzzleData?.filledCoordinates) {
                const cells = findWordInGrid(word, puzzleData.filledCoordinates);
                if (cells && cells.length > 0) {
                    lastClickedGridCellRef.current = { row: cells[0].row, col: cells[0].col };
                    const highlightCellsSameColor = cells.map((cell) => ({
                        row: cell.row,
                        col: cell.col,
                    }));
                    setHighlightCells(highlightCellsSameColor);
                    updateWordInputsForHighlightedCells(highlightCellsSameColor);
                    setSelectedWords([word]);
                } else {
                    lastClickedGridCellRef.current = null;
                    setHighlightCells([]);
                    setWordInputs({});
                    setSelectedWords([]);
                }
            } else {
                lastClickedGridCellRef.current = null;
                setHighlightCells([]);
                setWordInputs({});
                setSelectedWords([]);
            }
        },
        [puzzleData?.filledCoordinates, updateWordInputsForHighlightedCells]
    );

    const playStoryWordObjs = useMemo(() => {
        if (!puzzleData?.template) return [];
        return collectWordObjsForStoryMask({
            filledCoordinates: puzzleData.filledCoordinates,
            crossTpl: puzzleData.template,
            template: puzzleData.template,
        });
    }, [puzzleData]);

    const playStoryDialogChildren = useMemo(() => {
        if (!storyChromeLayout || !storyPresentation.bodyText?.trim()) return null;
        return (
            <MaskedStorySnippet
                text={storyPresentation.bodyText}
                wordObjs={playStoryWordObjs}
                highlightMode="underline"
                filledCoordinates={puzzleData?.filledCoordinates}
                resolvedWordsFound={wordsFound}
                component="span"
                sx={{ whiteSpace: "pre-wrap", lineHeight: 1.65 }}
            />
        );
    }, [
        storyChromeLayout,
        storyPresentation.bodyText,
        playStoryWordObjs,
        puzzleData?.filledCoordinates,
        wordsFound,
    ]);

    const playStoryAccordion = useMemo(() => {
        if (!storyChromeLayout || !playStoryDialogChildren) return null;
        const summaryTitle = String(storyPresentation.titleAboveGrid || "").trim()
            ? String(storyPresentation.titleAboveGrid).trim()
            : t("puzzle.playCross.story-card-heading");
        return (
            <StoryPlayStoryAccordion
                summaryTitle={summaryTitle}
                storyId={storyPresentation.storyId ?? undefined}
                showSlidingPuzzle={storyPresentation.showSlidingPuzzle}
                showJigsawPuzzle={storyPresentation.showJigsawPuzzle}
                defaultExpanded={false}
                compact
            >
                {playStoryDialogChildren}
            </StoryPlayStoryAccordion>
        );
    }, [
        storyChromeLayout,
        playStoryDialogChildren,
        storyPresentation.titleAboveGrid,
        storyPresentation.storyId,
        storyPresentation.showSlidingPuzzle,
        storyPresentation.showJigsawPuzzle,
        t,
    ]);

    // Re-merge meanings when lookup finishes or highlights/words change (avoids empty preview if state order differs).
    useEffect(() => {
        if (!highlightCells?.length || !puzzleData?.filledCoordinates?.length) return;
        updateWordInputsForHighlightedCells(highlightCells);
    }, [highlightCells, puzzleData?.filledCoordinates, updateWordInputsForHighlightedCells]);

    const handleClickedWords = (_word, cells) => {
        /** Clear selection — grid typing + highlights off */
        const clearWordSelection = () => {
            lastClickedGridCellRef.current = null;
            setHighlightCells([]);
            setWordInputs({});
            setSelectedWords([]);
            setSelectedWord(null);
        };

        if (!cells || cells.length === 0) {
            clearWordSelection();
            return;
        }

        const lastCell = cells[cells.length - 1];
        if (!lastCell) {
            clearWordSelection();
            return;
        }

        lastClickedGridCellRef.current = { row: lastCell.row, col: lastCell.col };

        /** Unfound clues whose cells include the clicked square — any letter can select the whole word. */
        const wordsContainingCell =
            puzzleData?.filledCoordinates?.filter((coord) => {
                if (!(coord.text || coord.word)) return false;
                if (!coord.positions?.length) return false;
                if (coord.direction === "scattered") return false;
                const nw = normalizeWord(coord.text || coord.word || "");
                if (!nw || wordsFound.includes(nw)) return false;
                return coord.positions.some(
                    (pos) => pos.row === lastCell.row && pos.col === lastCell.col
                );
            }) ?? [];

        if (wordsContainingCell.length === 0) {
            clearWordSelection();
            return;
        }

        const allHighlightCells = [];
        const selectedWordList = [];
        wordsContainingCell.forEach((coord) => {
            const label = coord.text || coord.word;
            if (label) selectedWordList.push(label);
            coord.positions.forEach((pos) => {
                if (!allHighlightCells.some((c) => c.row === pos.row && c.col === pos.col)) {
                    allHighlightCells.push({ row: pos.row, col: pos.col });
                }
            });
        });

        setHighlightCells(allHighlightCells);
        setSelectedWords(selectedWordList);
        setSelectedWord(null);

        updateWordInputsForHighlightedCells(allHighlightCells);
    };

    const crosswordWordInputKeysSig = Object.keys(wordInputs).sort().join("|");

    /** Active unsolved word for the letter-wheel assist (matches keyboard Across preference). */
    const activeWheelWord = useMemo(() => {
        const keys = Object.keys(wordInputs);
        if (keys.length === 0) return null;
        const cell = lastClickedGridCellRef.current;
        const throughCell = cell
            ? keys.filter((k) =>
                  wordInputs[k].cells?.some((c) => c.row === cell.row && c.col === cell.col),
              )
            : [];
        const pool = throughCell.length > 0 ? throughCell : keys;
        let wordKey = crosswordKeyboardWordKeyRef.current;
        if (!wordKey || !pool.includes(wordKey)) {
            wordKey = pool.find((k) => wordInputs[k].direction === "horizontal") ?? pool[0];
        }
        const wi = wordInputs[wordKey];
        if (!wi) return null;
        return {
            wordKey,
            expectedWord: wi.expectedWord || wi.word || "",
            meaningPreview: wi.meaningPreview || "",
            cells: wi.cells || [],
        };
        // highlightCells / selectedWords keep this in sync when the player changes selection
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [crosswordWordInputKeysSig, highlightCells, selectedWords, selectedWord, wordInputs]);

    /** Keyboard fill: active word from selection (Across wins at intersections until Tab). Letters fill slot 0, then 1… */
    useEffect(() => {
        const keys = Object.keys(wordInputs);
        if (keys.length === 0) {
            crosswordKeyboardWordKeyRef.current = null;
            return;
        }
        const cell = lastClickedGridCellRef.current;
        const matching = cell
            ? keys.filter((k) =>
                  wordInputs[k].cells.some((c) => c.row === cell.row && c.col === cell.col)
              )
            : [];
        const pool = matching.length > 0 ? matching : keys;
        let nextKey = crosswordKeyboardWordKeyRef.current;
        if (!nextKey || !pool.includes(nextKey)) {
            nextKey =
                pool.find((k) => wordInputs[k].direction === "horizontal") ?? pool[0];
        }
        crosswordKeyboardWordKeyRef.current = nextKey;

        const wi = wordInputs[nextKey];
        if (!wi?.cells?.length) return;
    }, [highlightCells, crosswordWordInputKeysSig]);


    // Handle word input change (immediate local update for display)
    const handleWordInputChange = (wordKey, value) => {
        const next = value || "";
        setLocalWordInputValues((prev) => ({ ...prev, [wordKey]: next }));
        debouncedSetWordInputValue(wordKey, next);
    };

    // Debounce heavy state update to avoid re-renders on every keypress
    const debouncedSetWordInputValue = useMemo(
        () =>
            debounce((wordKey, value) => {
                setWordInputs(prev => ({
                    ...prev,
                    [wordKey]: {
                        ...prev[wordKey],
                        inputValue: value
                    }
                }));
            }, 300),
        []
    );

    /** Per-cell typed letters for grid overlay (indices align with fill-strip spaced strings). */
    const cellDraftLetters = useMemo(() => {
        const out = {};
        Object.entries(wordInputs).forEach(([wordKey, wi]) => {
            const maxLen = Math.min(
                removeHyphens(wi.expectedWord).length,
                wi.cells?.length ?? 0
            );
            if (!maxLen || !wi.cells) return;
            const cur = localWordInputValues[wordKey] ?? wi.inputValue ?? "";
            const padded = cur.padEnd(maxLen, " ");
            for (let i = 0; i < maxLen; i++) {
                const c = padded[i];
                if (/[a-zA-Z]/i.test(c)) {
                    const cell = wi.cells[i];
                    out[`${cell.row},${cell.col}`] = c.toUpperCase();
                }
            }
        });
        return out;
    }, [wordInputs, localWordInputValues]);

    const cellDraftLettersRef = useRef({});
    cellDraftLettersRef.current = cellDraftLetters;
    const wordInputsRef = useRef(wordInputs);
    wordInputsRef.current = wordInputs;
    const handleWordInputChangeRef = useRef(handleWordInputChange);
    handleWordInputChangeRef.current = handleWordInputChange;

    useEffect(() => {
        const onKeyDown = (e) => {
            const wiMap = wordInputsRef.current;
            const keys = Object.keys(wiMap);
            if (keys.length === 0) return;

            const el = document.activeElement;
            const tag = el?.tagName?.toLowerCase();
            if (tag === "input" || tag === "textarea" || tag === "select" || el?.isContentEditable) {
                return;
            }

            let wordKey = crosswordKeyboardWordKeyRef.current;
            if (!wordKey || !(wordKey in wiMap)) {
                wordKey = keys[0];
                crosswordKeyboardWordKeyRef.current = wordKey;
            }
            const wi = wiMap[wordKey];
            if (!wi?.cells?.length) return;

            const maxLen = Math.min(removeHyphens(wi.expectedWord).length, wi.cells.length);

            const lastCell = lastClickedGridCellRef.current;
            const matching = lastCell
                ? keys.filter((k) =>
                      wiMap[k].cells.some((c) => c.row === lastCell.row && c.col === lastCell.col)
                  )
                : [];
            const pool = matching.length > 0 ? matching : keys;

            if (e.key === "Tab") {
                if (pool.length > 1) {
                    e.preventDefault();
                    const ix = Math.max(0, pool.indexOf(wordKey));
                    const nextKey = pool[(ix + 1) % pool.length];
                    crosswordKeyboardWordKeyRef.current = nextKey;
                }
                return;
            }

            if (e.key === "Backspace") {
                e.preventDefault();
                const merged = { ...cellDraftLettersRef.current };
                const lastIdx = lastFilledSlotIndex(wi, merged, maxLen);
                if (lastIdx < 0) return;
                const prevCell = wi.cells[lastIdx];
                delete merged[`${prevCell.row},${prevCell.col}`];
                keys.forEach((wk) => {
                    const w = wiMap[wk];
                    const touches = w.cells.some(
                        (c) => c.row === prevCell.row && c.col === prevCell.col
                    );
                    if (!touches) return;
                    handleWordInputChangeRef.current(wk, crosswordSpacedStringFromDraftMap(w, merged));
                });
                crosswordKeyboardWordKeyRef.current = wordKey;
                return;
            }

            if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
            if (!/^[a-zA-Z]$/.test(e.key)) return;
            e.preventDefault();

            const letter = e.key.toUpperCase();
            const merged = { ...cellDraftLettersRef.current };
            const insertIdx = firstEmptySlotIndex(wi, merged, maxLen);
            if (insertIdx < 0) return;
            const curCell = wi.cells[insertIdx];
            merged[`${curCell.row},${curCell.col}`] = letter;

            keys.forEach((wk) => {
                const w = wiMap[wk];
                const touches = w.cells.some(
                    (c) => c.row === curCell.row && c.col === curCell.col
                );
                if (!touches) return;
                handleWordInputChangeRef.current(wk, crosswordSpacedStringFromDraftMap(w, merged));
            });

            crosswordKeyboardWordKeyRef.current = wordKey;
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    // Clear local values when a word is removed from wordInputs (e.g. after correct answer)
    useEffect(() => {
        setLocalWordInputValues(prev => {
            const next = { ...prev };
            let changed = false;
            Object.keys(prev).forEach(key => {
                if (!(key in wordInputs)) {
                    delete next[key];
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [wordInputs]);

    useEffect(() => {
        return () => debouncedSetWordInputValue.cancel();
    }, [debouncedSetWordInputValue]);

    // Handle word input submission
    const handleWordInputSubmit = (wordKey) => {
        const wordInput = wordInputs[wordKey];
        if (!wordInput) return;

        debouncedSetWordInputValue.flush();
        const inputValue = localWordInputValues[wordKey] ?? wordInput.inputValue ?? '';
        const normalizedInput = normalizeWord(inputValue.replace(/\s+/g, ""));
        const normalizedExpected = normalizeWord(wordInput.expectedWord);

        // Remove hyphens for comparison
        const inputWithoutHyphens = removeHyphens(normalizedInput);
        const expectedWithoutHyphens = removeHyphens(normalizedExpected);

        // Check if words match (with or without hyphens)
        if (normalizedInput === normalizedExpected || inputWithoutHyphens === expectedWithoutHyphens) {
            // Word is correct! Mark it as found
            const wordToAdd = normalizedExpected;
            if (!wordsFound.includes(wordToAdd)) {
                const updatedWordsFound = [...wordsFound, wordToAdd];
                setWordsFound(updatedWordsFound);
                playWordResolvedChime();
                recordCrossPlayHintUsedIfApplicable(wordToAdd, wordsFound, wordInput.expectedWord);

                // Mark cells as found in the grid
                if (puzzleGridRef.current) {
                    puzzleGridRef.current.markClickedCellsAsFound(wordInput.cells);
                }
                triggerWordRevealCelebration(wordInput.expectedWord, wordInput.cells);

                // Update progress API
                if (userInfo?.cloudUser?.id) {
                    CrossService.progressPuzzle(userInfo.cloudUser.id, crossTplId, updatedWordsFound)
                        .catch((error) => {
                            Logger.error('Error updating progress:', error);
                        });
                }

                // Remove this word from inputs
                const newWordInputs = { ...wordInputs };
                delete newWordInputs[wordKey];
                setWordInputs(newWordInputs);

                // Clear highlights
                setHighlightCells([]);

                setSnackbarMessage(t("puzzle.playCross.found", { word: wordInput.expectedWord }));
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
            }
        } else {
            setSnackbarMessage(t("puzzle.playCross.incorrect"));
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    // Auto-resolve when typed letters match the answer (grid + fill strip) — same outcome as ✓ submit.
    useEffect(() => {
        const keys = Object.keys(wordInputs);
        if (keys.length === 0) return;

        for (const wordKey of keys) {
            const wordInput = wordInputs[wordKey];
            if (!wordInput?.cells?.length) continue;

            const rawInput = localWordInputValues[wordKey] ?? wordInput.inputValue ?? "";
            if (!crossWordAllSlotsFilled(rawInput, wordInput.expectedWord, wordInput.cells.length)) {
                continue;
            }

            const normalizedInput = normalizeWord(rawInput.replace(/\s+/g, ""));
            const normalizedExpected = normalizeWord(wordInput.expectedWord);
            const inputWithoutHyphens = removeHyphens(normalizedInput);
            const expectedWithoutHyphens = removeHyphens(normalizedExpected);
            const matches =
                normalizedInput === normalizedExpected ||
                inputWithoutHyphens === expectedWithoutHyphens;
            if (!matches) continue;

            const wordToAdd = normalizedExpected;
            if (wordsFound.includes(wordToAdd)) continue;

            debouncedSetWordInputValue.flush();

            const updatedWordsFound = [...wordsFound, wordToAdd];
            setWordsFound(updatedWordsFound);
            playWordResolvedChime();
            recordCrossPlayHintUsedIfApplicable(wordToAdd, wordsFound, wordInput.expectedWord);

            if (puzzleGridRef.current) {
                puzzleGridRef.current.markClickedCellsAsFound(wordInput.cells);
            }
            triggerWordRevealCelebration(wordInput.expectedWord, wordInput.cells);

            if (userInfo?.cloudUser?.id) {
                CrossService.progressPuzzle(userInfo.cloudUser.id, crossTplId, updatedWordsFound).catch(
                    (error) => {
                        Logger.error("Error updating progress:", error);
                    }
                );
            }

            setWordInputs((prev) => {
                const next = { ...prev };
                delete next[wordKey];
                return next;
            });

            setHighlightCells([]);

            setSnackbarMessage(t("puzzle.playCross.found", { word: wordInput.expectedWord }));
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            break;
        }
    }, [
        wordInputs,
        localWordInputValues,
        wordsFound,
        debouncedSetWordInputValue,
        recordCrossPlayHintUsedIfApplicable,
        playWordResolvedChime,
        triggerWordRevealCelebration,
        userInfo?.cloudUser?.id,
        crossTplId,
        t,
    ]);

    // Handle word entered from Word Clues panel (Enter icon popover)
    const handleWordEnterSubmit = (expectedWord, enteredValue) => {
        if (!puzzleData?.filledCoordinates || !expectedWord) return true;
        const normalizedInput = normalizeWord((enteredValue || "").replace(/\s+/g, ""));
        const normalizedExpected = normalizeWord(expectedWord);
        const inputWithoutHyphens = removeHyphens(normalizedInput);
        const expectedWithoutHyphens = removeHyphens(normalizedExpected);
        if (normalizedInput === normalizedExpected || inputWithoutHyphens === expectedWithoutHyphens) {
            const wordToAdd = normalizedExpected;
            if (!wordsFound.includes(wordToAdd)) {
                const updatedWordsFound = [...wordsFound, wordToAdd];
                setWordsFound(updatedWordsFound);
                playWordResolvedChime();
                recordCrossPlayHintUsedIfApplicable(wordToAdd, wordsFound, expectedWord);
                const cells = findWordInGrid(wordToAdd, puzzleData.filledCoordinates);
                if (cells?.length > 0 && puzzleGridRef.current) {
                    puzzleGridRef.current.markClickedCellsAsFound(cells);
                }
                if (cells?.length > 0) {
                    triggerWordRevealCelebration(expectedWord, cells);
                }
                if (userInfo?.cloudUser?.id) {
                    CrossService.progressPuzzle(userInfo.cloudUser.id, crossTplId, updatedWordsFound).catch((err) => Logger.error("Progress update error:", err));
                }
                setHighlightCells([]);
                setWordInputs({});
                setSnackbarMessage(t("puzzle.playCross.found", { word: expectedWord }));
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
            }
            return true;
        }
        setSnackbarMessage(t("puzzle.playCross.incorrect"));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return false;
    };

    const handleSubscribe = () => {
        setUserSubscription('premium');
        setSubscriptionModalOpen(false);
    };

    const handleCopyLink = () => {
        setSnackbarMessage(t("puzzle.playCross.link-copied"));
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
    };

    // Handle typed word input - highlights matching characters as user types
    const handleTypedWordChange = (event) => {
        const value = event.target.value;
        setTypedWord(value);

        if (!value || !value.trim()) {
            setHighlightCells([]);
            setWordInputs({});
            return;
        }

        const normalizedValue = normalizeWord(value);

        // Find all matching cells for the typed text (partial word matching)
        const cells = findMatchingCellsAsTyped(normalizedValue, puzzleData?.grid);
        setHighlightCells(cells);
    };

    // Handle Enter key to submit typed word
    const handleTypedWordKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSubmitTypedWord();
        }
    };

    // Submit typed word if it matches - just highlight, don't mark as found
    const handleSubmitTypedWord = () => {
        if (!typedWord?.trim() || !puzzleData?.words) return;

        const normalizedTypedWord = normalizeWord(typedWord);
        const matchingWord = findMatchingWord(normalizedTypedWord, puzzleData.words);

        if (!matchingWord) {
            setSnackbarMessage(t("puzzle.playCross.word-not-in-list"));
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        // Find the word in the grid and highlight it
        const cells = findWordInGrid(normalizedTypedWord, puzzleData.filledCoordinates);
        if (cells && cells.length > 0) {
            const highlightCellsSameColor = cells.map((cell) => ({
                row: cell.row,
                col: cell.col
            }));
            setHighlightCells(highlightCellsSameColor);
        } else {
            setSnackbarMessage(t("puzzle.playCross.word-not-in-grid"));
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        // Clear the input
        setTypedWord('');
    };

    if (loading) {
        return (
            <MistPlayShell sx={playShellSx}>
                <Box
                    sx={{
                        display: "flex",
                        flex: 1,
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography variant="h6" sx={MIST_STATUS_TEXT_SX}>
                        {t("puzzle.playCross.loading")}
                    </Typography>
                </Box>
            </MistPlayShell>
        );
    }

    if (!puzzleData) {
        return (
            <MistPlayShell sx={playShellSx}>
                <Box
                    sx={{
                        display: "flex",
                        flex: 1,
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Typography variant="h6" sx={MIST_STATUS_TEXT_SX}>
                        {t("puzzle.playCross.not-found")}
                    </Typography>
                    <Typography variant="body2" sx={MIST_STATUS_TEXT_SX}>
                        {t("puzzle.playCross.not-found-desc", { id: crossTplId })}
                    </Typography>
                    <Typography variant="body2" sx={MIST_STATUS_TEXT_SX}>
                        {t("puzzle.playCross.not-found-hint")}
                    </Typography>
                </Box>
            </MistPlayShell>
        );
    }

    // Paid puzzle: wait for user check, then block if no access
    if (showAccessCheck && !settingsLoaded) {
        return (
            <MistPlayShell sx={playShellSx}>
                <Box
                    sx={{
                        display: "flex",
                        flex: 1,
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography variant="body1" sx={MIST_STATUS_TEXT_SX}>
                        {t("puzzle.playCross.checking-access")}
                    </Typography>
                </Box>
            </MistPlayShell>
        );
    }
    if (showAccessCheck && settingsLoaded && !canAccessPuzzle) {
        return (
            <MistPlayShell>
                <Box sx={{ width: "100%" }}>
                    <PuzzleAccessGate
                        isAuthenticated={isAuthenticated}
                        userPayPlan={UtilService.resolveWebPayPlan(userInfo?.cloudUser)}
                        onUpgradeClick={() => setSubscriptionModalOpen(true)}
                        puzzleTitle={crosswordPageDisplayTitle || puzzleData?.title}
                        onBackClick={handleBackClick}
                    />
                    <SubscriptionDialog
                        open={subscriptionModalOpen}
                        onClose={() => setSubscriptionModalOpen(false)}
                        onSubscribe={handleSubscribe}
                        triggerReason={subscriptionTriggerReason}
                        userSubscription={userSubscription}
                    />
                </Box>
            </MistPlayShell>
        );
    }

    if (
        needsPremiumCreditCharge
        && (premiumCreditStatus === "insufficient" || premiumCreditStatus === "error")
    ) {
        return (
            <MistPlayShell>
                <Box sx={{ width: "100%", p: { xs: 2, sm: 3 } }}>
                    <Alert severity={premiumCreditStatus === "insufficient" ? "warning" : "error"} sx={{ mb: 2 }}>
                        {premiumCreditStatus === "insufficient"
                            ? t("core.puzzleAccess.insufficientCredits")
                            : (premiumCreditError || t("core.puzzleAccess.creditChargeError"))}
                    </Alert>
                    {premiumCreditStatus === "insufficient" && (
                        <Typography
                            variant="body2"
                            sx={{ mb: 2, textAlign: "center", ...MIST_STATUS_TEXT_SX }}
                        >
                            {t("core.puzzleAccess.insufficientCreditsHint")}
                        </Typography>
                    )}
                    <PuzzleAccessGate
                        isAuthenticated={isAuthenticated}
                        userPayPlan={UtilService.resolveWebPayPlan(userInfo?.cloudUser)}
                        onUpgradeClick={() => setSubscriptionModalOpen(true)}
                        puzzleTitle={crosswordPageDisplayTitle || puzzleData?.title}
                        onBackClick={handleBackClick}
                    />
                    <SubscriptionDialog
                        open={subscriptionModalOpen}
                        onClose={() => setSubscriptionModalOpen(false)}
                        onSubscribe={handleSubscribe}
                        triggerReason={subscriptionTriggerReason}
                        userSubscription={userSubscription}
                    />
                </Box>
            </MistPlayShell>
        );
    }

    if (needsPremiumCreditCharge && premiumCreditStatus !== "ok") {
        return (
            <MistPlayShell sx={playShellSx}>
                <Box
                    sx={{
                        display: "flex",
                        flex: 1,
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography variant="body1" sx={MIST_STATUS_TEXT_SX}>
                        {t("puzzle.playCross.checking-access")}
                    </Typography>
                </Box>
            </MistPlayShell>
        );
    }

    return (
        <MistPlayShell sx={playShellSx}>
            {/* Main layout (md+): crossword grid vs clues — 6:6 when panel open, full width grid when closed */}
            <Grid container spacing={2} sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', p: isMobile ? 1 : 2, backgroundColor: 'transparent', flex: hasSiteAdBanner ? '0 0 auto' : 1, alignContent: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                {/* Crossword board + toolbar — full width when clues closed */}
                <Grid
                    size={{ xs: 12, md: wordsDrawerOpen ? 6 : 12 }}
                    sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}
                >
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    borderRadius: 2,
                    boxSizing: 'border-box',
                    ...FROSTED_PANEL_SX,
                }}>
                    {/* Top Action Bar */}
                    <Box sx={{
                        pt: (theme) =>
                            `calc(${theme.spacing(isMobile ? 0.25 : 0.5)} + 2px)`,
                        px: isMobile ? 1 : 2,
                        pb: isMobile ? 0.25 : 0.5,
                        flexShrink: 0,
                        overflowX: 'visible',
                        overflowY: 'hidden'
                    }}>
                        {/* Toolbar: back | spacer | timer+pause + share + actions */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            minWidth: 0,
                            boxSizing: 'border-box',
                            px: isMobile ? 0.5 : 0,
                            gap: 0.5,
                        }}>
                            {/* Back Button */}
                            <Tooltip title={t("puzzle.playCross.go-back")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={handleBackClick}
                                    sx={{
                                        ...playPillButtonSx,
                                        ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                    }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                            </Tooltip>

                            {settingsLoaded ? (
                                <Box
                                    ref={bgmButtonWrapRef}
                                    aria-hidden
                                    sx={{
                                        position: "fixed",
                                        left: -9999,
                                        top: 0,
                                        width: 1,
                                        height: 1,
                                        overflow: "hidden",
                                        opacity: 0,
                                        pointerEvents: "none",
                                    }}
                                >
                                    <AudioButton
                                        key={`bgm-${bgmAutoPlay}`}
                                        tracks={PUZZLE_BGM_TRACKS}
                                        defaultPlaying={bgmAutoPlay}
                                        cloudUserId={userInfo?.cloudUser?.id}
                                        leadingDivider={false}
                                        onPlayingChange={setBgmPlaying}
                                    />
                                </Box>
                            ) : null}

                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: 1,
                                }}
                            >
                                {letterHintConsumedVisible ? (
                                    <Box
                                        component="span"
                                        sx={{
                                            flexShrink: 1,
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                            lineHeight: 1.2,
                                            textAlign: "center",
                                            bgcolor: "#0B3D2E",
                                            color: "#E7FFF4",
                                            border: "1px solid #2E7D57",
                                            boxShadow: 1,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            maxWidth: "100%",
                                        }}
                                    >
                                        {t("puzzle.playCross.letter-hint-consumed")}
                                    </Box>
                                ) : null}
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    flexShrink: 0,
                                    flexWrap: isMobile ? "wrap" : "nowrap",
                                }}
                            >
                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0, flexShrink: 0 }}>
                                    <PlayElapsedTimer
                                        running={timerRunning}
                                        ariaLabel={t("puzzle.play-elapsed-time", "Elapsed time")}
                                    />
                                    {!isCrosswordComplete ? (
                                        <Tooltip
                                            title={
                                                timerPaused || visibilityPaused
                                                    ? t("puzzle.jigsaw.resume-timer", "Resume timer")
                                                    : t("puzzle.jigsaw.pause-timer", "Pause timer")
                                            }
                                            arrow
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    if (visibilityPaused) {
                                                        continuePlaying();
                                                        setTimerPaused(false);
                                                        return;
                                                    }
                                                    setTimerPaused((p) => !p);
                                                }}
                                                sx={{
                                                    ...playPillButtonSx,
                                                    flexShrink: 0,
                                                    color: ACCENT.light,
                                                    ml: -0.75,
                                                    borderRadius: "50%",
                                                    ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                                }}
                                                aria-label={
                                                    timerPaused || visibilityPaused
                                                        ? t("puzzle.jigsaw.resume-timer", "Resume timer")
                                                        : t("puzzle.jigsaw.pause-timer", "Pause timer")
                                                }
                                                aria-pressed={timerPaused || visibilityPaused}
                                            >
                                                {timerPaused || visibilityPaused ? (
                                                    <PlayArrowIcon fontSize="small" />
                                                ) : (
                                                    <PauseIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    ) : null}
                                </Box>

                                <SocialShareDropdown
                                    compact
                                    puzzleTitle={crosswordPageDisplayTitle || puzzleData?.title || 'Puzzle'}
                                    puzzleId={crossTplId}
                                    puzzleType="CROSSWORD"
                                    onCopyLink={handleCopyLink}
                                    imageUrl={crosswordPreviewImage}
                                />

                            <Tooltip title={t("puzzle.playCross.download-pdf", "Download PDF")} arrow>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            void handlePdf();
                                        }}
                                        disabled={isPdfDownloading || !canDownloadPdf}
                                        sx={{
                                            ...playPillButtonSx,
                                            ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                            borderRadius: "50%",
                                            flexShrink: 0,
                                        }}
                                        aria-label={t("puzzle.playCross.download-pdf", "Download PDF")}
                                    >
                                        {isPdfDownloading ? (
                                            <CircularProgress size={16} thickness={5} sx={{ color: ACCENT.light }} />
                                        ) : (
                                            <PictureAsPdfIcon />
                                        )}
                                    </IconButton>
                                </span>
                            </Tooltip>

                            {/* Clear Selection Button */}
                            <Tooltip title={t("puzzle.playCross.clear-selection")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={handleClearClickedWords}
                                    sx={{
                                        ...playPillButtonSx,
                                        ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                    }}
                                >
                                    <RestartAltIcon />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title={t("nav.settings", "Settings")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={(e) => setSettingsAnchor(e.currentTarget)}
                                    sx={{
                                        ...playPillButtonSx,
                                        ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                        ...(settingsAnchor
                                            ? {
                                                  backgroundColor: "#3BA666",
                                                  color: "#fff",
                                                  "&:hover": { backgroundColor: "#2E8B57" },
                                              }
                                            : null),
                                        borderRadius: "50%",
                                        flexShrink: 0,
                                    }}
                                    aria-label={t("nav.settings", "Settings")}
                                    aria-haspopup="true"
                                    aria-expanded={Boolean(settingsAnchor)}
                                >
                                    <SettingsIcon />
                                </IconButton>
                            </Tooltip>

                            {/* Words List Toggle Button */}
                            <Tooltip title={t("puzzle.playCross.words-meaning")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => setWordsDrawerOpen(!wordsDrawerOpen)}
                                    sx={{
                                        ...playPillButtonSx,
                                        ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                        ...(wordsDrawerOpen
                                            ? {
                                                  backgroundColor: '#3BA666',
                                                  color: '#fff',
                                                  '&:hover': { backgroundColor: '#2E8B57' },
                                              }
                                            : null),
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <LibraryBooksIcon />
                                </IconButton>
                            </Tooltip>
                            </Box>
                        </Box>
                        <Popover
                            open={Boolean(settingsAnchor)}
                            anchorEl={settingsAnchor}
                            onClose={() => setSettingsAnchor(null)}
                            disableScrollLock
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                            slotProps={{
                                paper: {
                                    sx: {
                                        mt: 0.75,
                                        p: 1.5,
                                        minWidth: 220,
                                        borderRadius: 2,
                                        boxShadow: 4,
                                    },
                                },
                            }}
                        >
                            {isAuthenticated ? (
                                <>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
                                    >
                                        {t("puzzle.sliding.settings-wallet", "Balance")}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr 1fr",
                                            gap: 0.75,
                                            mb: 1.25,
                                        }}
                                    >
                                        {[
                                            {
                                                key: "play",
                                                label: t("puzzle.sliding.settings-play-credits", "Play"),
                                                value: walletPlayCredits,
                                                icon: <TollIcon sx={{ fontSize: "1.1rem", color: "#2761F5" }} />,
                                            },
                                            {
                                                key: "hints",
                                                label: t("puzzle.sliding.settings-hint-credits", "Hints"),
                                                value: walletHintCredits,
                                                icon: (
                                                    <LightbulbOutlinedIcon
                                                        sx={{ fontSize: "1.1rem", color: ACCENT.light }}
                                                    />
                                                ),
                                            },
                                            {
                                                key: "coins",
                                                label: t("puzzle.sliding.settings-coins", "Coins"),
                                                value: walletPuzzleCoins,
                                                icon: (
                                                    <MonetizationOnIcon
                                                        sx={{ fontSize: "1.1rem", color: "#d97706" }}
                                                    />
                                                ),
                                            },
                                        ].map((item) => (
                                            <Box
                                                key={item.key}
                                                sx={{
                                                    textAlign: "center",
                                                    py: 0.75,
                                                    px: 0.5,
                                                    borderRadius: 1,
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    bgcolor: "action.hover",
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: "block", fontWeight: 600, lineHeight: 1.2 }}
                                                >
                                                    {item.label}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: 0.35,
                                                        mt: 0.25,
                                                    }}
                                                >
                                                    {item.icon}
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 800, lineHeight: 1.2 }}
                                                    >
                                                        {walletBalancesLoading && item.value == null
                                                            ? "…"
                                                            : Number(item.value ?? 0).toLocaleString()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                    <Divider sx={{ my: 1.25 }} />
                                </>
                            ) : null}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {isBoldFont
                                            ? t("puzzle.playGrid.switch-light")
                                            : t("puzzle.playGrid.switch-bold")}
                                    </Typography>
                                    <Tooltip
                                        title={
                                            isBoldFont
                                                ? t("puzzle.playGrid.switch-light")
                                                : t("puzzle.playGrid.switch-bold")
                                        }
                                        arrow
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() => setIsBoldFont((v) => !v)}
                                            sx={{
                                                color: isBoldFont ? ACCENT.light : "text.secondary",
                                                ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                            }}
                                            aria-label={
                                                isBoldFont
                                                    ? t("puzzle.playGrid.switch-light")
                                                    : t("puzzle.playGrid.switch-bold")
                                            }
                                            aria-pressed={isBoldFont}
                                        >
                                            <FormatBoldIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Divider sx={{ my: 0.5 }} />
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {t("puzzle.jigsaw.background-music", "Background music")}
                                    </Typography>
                                    <Tooltip
                                        title={bgmPlaying ? t("audio-button.mute") : t("audio-button.play")}
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    const btn = bgmButtonWrapRef.current?.querySelector("button");
                                                    btn?.click();
                                                }}
                                                sx={{
                                                    color: ACCENT.light,
                                                    ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                                }}
                                                aria-label={
                                                    bgmPlaying ? t("audio-button.mute") : t("audio-button.play")
                                                }
                                            >
                                                {bgmPlaying ? (
                                                    <MusicNoteIcon fontSize="small" />
                                                ) : (
                                                    <MusicOffIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {t("puzzle.jigsaw.sound-effects", "Sound effects")}
                                    </Typography>
                                    <Tooltip
                                        title={
                                            soundEnabled
                                                ? t("puzzle.jigsaw.mute-sound-effects", "Mute sound effects")
                                                : t("puzzle.jigsaw.enable-sound-effects", "Enable sound effects")
                                        }
                                        arrow
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={handleToggleSoundEffects}
                                            sx={{
                                                color: ACCENT.light,
                                                ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                            }}
                                            aria-label={
                                                soundEnabled
                                                    ? t("puzzle.jigsaw.mute-sound-effects", "Mute sound effects")
                                                    : t(
                                                          "puzzle.jigsaw.enable-sound-effects",
                                                          "Enable sound effects",
                                                      )
                                            }
                                            aria-pressed={!soundEnabled}
                                        >
                                            {soundEnabled ? (
                                                <VolumeUpIcon fontSize="small" />
                                            ) : (
                                                <VolumeOffIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Popover>
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'visible',
                        pt: 0.5,
                        pl: 2, pr: 2,
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        pb: 2,
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        position: 'relative',
                        boxSizing: 'border-box'
                    }}>
                        <Box sx={{
                            width: 'min(100%, calc(100dvh - 340px))',
                            maxWidth: '100%',
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                            alignSelf: 'center',
                            minWidth: 0,
                            aspectRatio: '1',
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                            flexShrink: 0,
                        }}>
                            <CrossGrid
                                key={crossTplId}
                                ref={puzzleGridRef}
                                grid={puzzleData.grid}
                                words={puzzleData.words}
                                wordsFound={wordsFound}
                                onClearClickedWords={handleClearClickedWords}
                                onClickedWords={handleClickedWords}
                                gridSize={12}
                                isBoldFont={isBoldFont}
                                filledCoordinates={puzzleData.filledCoordinates}
                                hintRevealCells={displayHintRevealCells}
                                highlightCells={highlightCells}
                                hideAllWords={HIDE_ALL_WORDS}
                                cellDraftLetters={cellDraftLetters}
                                clueNumberFontPx={isAdminRole ? adminGridClueNumberFontPx : undefined}
                                cellFontPx={isAdminRole ? adminGridCellFontPx : undefined}
                                celebrateCellIndexByKey={celebrateCellIndexByKey}
                            />
                        </Box>

                        {!isCrosswordComplete ? (
                            <Box sx={{ width: "100%", flexShrink: 0 }}>
                                <CrosswordScrambleRack
                                    wordKey={activeWheelWord?.wordKey || ""}
                                    expectedWord={activeWheelWord?.expectedWord || ""}
                                    cells={activeWheelWord?.cells || []}
                                    hintRevealCells={displayHintRevealCells}
                                    foundCellKeySet={foundCellKeySet}
                                    meaningPreview={activeWheelWord?.meaningPreview || ""}
                                    onLettersChange={(letters) => {
                                        if (!activeWheelWord?.wordKey) return;
                                        handleWordInputChange(activeWheelWord.wordKey, letters);
                                    }}
                                    onCorrectPlacement={(slotIndexes) => {
                                        const wordCells = activeWheelWord?.cells || [];
                                        const celebrate = {};
                                        (slotIndexes || []).forEach((index) => {
                                            const cell = wordCells[index];
                                            if (cell && typeof cell.row === "number" && typeof cell.col === "number") {
                                                celebrate[`${cell.row},${cell.col}`] = 0;
                                            }
                                        });
                                        if (Object.keys(celebrate).length === 0) return;
                                        setCelebrateCellIndexByKey((prev) => ({ ...(prev || {}), ...celebrate }));
                                        if (celebrateClearTimerRef.current) {
                                            window.clearTimeout(celebrateClearTimerRef.current);
                                        }
                                        celebrateClearTimerRef.current = window.setTimeout(() => {
                                            setCelebrateCellIndexByKey(null);
                                            celebrateClearTimerRef.current = null;
                                        }, WORD_REVEAL_ILLUMINATE_MS + 80);
                                    }}
                                />
                            </Box>
                        ) : null}

                        {!wordsDrawerOpen && puzzleData ? (
                            <CrosswordPlayProgressAside
                                placement="underGrid"
                                isAdminRole={isAdminRole}
                                wordInputs={wordInputs}
                                localWordInputValues={localWordInputValues}
                                hintRevealCells={displayHintRevealCells}
                                foundCellKeySet={foundCellKeySet}
                                onWordInputChange={handleWordInputChange}
                                onWordInputSubmit={handleWordInputSubmit}
                                adminGridClueNumberFontPx={adminGridClueNumberFontPx}
                                adminGridCellFontPx={adminGridCellFontPx}
                                onAdminGridClueNumberFontPxChange={setAdminGridClueNumberFontPx}
                                onAdminGridCellFontPxChange={setAdminGridCellFontPx}
                                t={t}
                                manualValidation={manualValidation}
                                typedWord={typedWord}
                                onTypedWordChange={handleTypedWordChange}
                                onTypedWordKeyDown={handleTypedWordKeyDown}
                                userRole={userInfo?.cloudUser?.userRole}
                                isCrosswordComplete={isCrosswordComplete}
                                puzzleWordsLength={puzzleData?.words?.length ?? 0}
                                wordsFoundLength={wordsFound.length}
                                settingsLoaded={settingsLoaded}
                                isAuthenticated={isAuthenticated}
                                completionActionsDisabled={completionActionsDisabled}
                                setFeedbackDialogOpen={setFeedbackDialogOpen}
                                handleBackClick={handleBackClick}
                                handleSignUpClick={handleSignUpClick}
                            />
                        ) : null}
                        <CopyrightLine />
                    </Box>
                </Box>
                </Grid>

                {/* Word clues sidebar — optional story opens from book icon */}
                {wordsDrawerOpen ? (
                <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <Box sx={{
                        ...FROSTED_PANEL_SX,
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'visible'
                    }}>
                        {!puzzleData ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, p: 3 }}>
                                <LinearProgress sx={{ width: '100%', mb: 2 }} />
                                <Typography variant="body2" color="text.secondary">{t("puzzle.playCross.loading-clues")}</Typography>
                            </Box>
                        ) : (
                        <CrossWordCluesPanel
                            filledCoordinates={puzzleData?.filledCoordinates || []}
                            wordsFound={wordsFound}
                            assistantMagicVisible={showAssistantMagic}
                            assistantMagicTooltipTitle={
                                canUseAssistant
                                    ? t(
                                          "puzzle.playCross.ai-chat-tooltip",
                                          "Open Chat with AI in the word list"
                                      )
                                    : t(
                                          "puzzle.playCross.assistant-magic-sign-in",
                                          "AI Assistant"
                                      )
                            }
                            assistantInputLocked={cluesAiMode && !canUseAssistant}
                            assistantLockedInputText={t(
                                "puzzle.playCross.assistant-placeholder",
                                "Ask for a hint about this puzzle"
                            )}
                            onOpenChatWithAi={
                                showAssistantMagic ? () => setCluesAiMode(true) : undefined
                            }
                            onHide={() => {
                                setCluesAiMode(false);
                                setWordsDrawerOpen(false);
                            }}
                            selectedWord={selectedWord}
                            selectedWords={selectedWords}
                            hintRevealCells={displayHintRevealCells}
                            onWordEnterSubmit={handleWordEnterSubmit}
                            hideAllWords={HIDE_ALL_WORDS}
                            userPayPlan={UtilService.resolveWebPayPlan(userInfo?.cloudUser)}
                            language={puzzleData?.template?.language || puzzleData?.language || 'english'}
                            tags={tagsForMeaning}
                            crossTplId={puzzleData?.templateId ?? puzzleData?.template?.id ?? null}
                            displayClue={
                                puzzleData?.displayClue ??
                                puzzleData?.display_clue ??
                                puzzleData?.template?.displayClue ??
                                puzzleData?.template?.display_clue ??
                                null
                            }
                            wordNetwork={hasWordNetwork ? wordNetworkData : null}
                            isPuzzleComplete={isCrosswordComplete}
                            chatWithAiOpen={cluesAiMode}
                            onCloseChatWithAi={() => setCluesAiMode(false)}
                            assistantMessages={assistantMessages}
                            assistantPrompt={assistantPrompt}
                            onAssistantPromptChange={setAssistantPrompt}
                            onAssistantSend={handleAssistantSubmit}
                            assistantLoading={assistantLoading}
                            assistantHistoryLoading={assistantHistoryLoading}
                            assistantCreditsTotal={userInfo?.cloudUser?.assistantCreditsTotal}
                            chatWithAiTitle={t("puzzle.playCross.chat-with-ai", "Chat with AI")}
                            chatWithAiPlaceholder={t(
                                "puzzle.playCross.assistant-placeholder",
                                "Ask for a hint about this puzzle"
                            )}
                            onWordSelect={handleWordSelectForPlay}
                            onOpenStory={
                                hasPlayStoryContent ? () => setStoryDialogOpen(true) : undefined
                            }
                            storySlot={playStoryAccordion}
                            bankLockIn={bankLockIn}
                        />
                        )}
                    </Box>
                        {puzzleData ? (
                            <CrosswordPlayProgressAside
                                placement="sidebar"
                                isAdminRole={isAdminRole}
                                wordInputs={wordInputs}
                                localWordInputValues={localWordInputValues}
                                hintRevealCells={displayHintRevealCells}
                                foundCellKeySet={foundCellKeySet}
                                onWordInputChange={handleWordInputChange}
                                onWordInputSubmit={handleWordInputSubmit}
                                adminGridClueNumberFontPx={adminGridClueNumberFontPx}
                                adminGridCellFontPx={adminGridCellFontPx}
                                onAdminGridClueNumberFontPxChange={setAdminGridClueNumberFontPx}
                                onAdminGridCellFontPxChange={setAdminGridCellFontPx}
                                t={t}
                                manualValidation={manualValidation}
                                typedWord={typedWord}
                                onTypedWordChange={handleTypedWordChange}
                                onTypedWordKeyDown={handleTypedWordKeyDown}
                                userRole={userInfo?.cloudUser?.userRole}
                                isCrosswordComplete={isCrosswordComplete}
                                puzzleWordsLength={puzzleData?.words?.length ?? 0}
                                wordsFoundLength={wordsFound.length}
                                settingsLoaded={settingsLoaded}
                                isAuthenticated={isAuthenticated}
                                completionActionsDisabled={completionActionsDisabled}
                                setFeedbackDialogOpen={setFeedbackDialogOpen}
                                handleBackClick={handleBackClick}
                                handleSignUpClick={handleSignUpClick}
                            />
                        ) : null}
                    </Box>
                </Grid>
                ) : null}
            </Grid>

            {/* Snackbar */}
            <CustomSnackbar
                open={snackbarOpen}
                setOpen={setSnackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />

            {hasPlayStoryContent ? (
                <StoryPlayStoryDialog
                    open={storyDialogOpen}
                    onClose={() => setStoryDialogOpen(false)}
                    summaryTitle={
                        String(storyPresentation.titleAboveGrid || "").trim()
                            ? String(storyPresentation.titleAboveGrid).trim()
                            : t("puzzle.playCross.story-card-heading")
                    }
                    imageUrl={storyPresentation.imageUrl || undefined}
                    storyShareId={storyShareIdFromPayload || undefined}
                    storyShareTitle={storyShareTitleForShare}
                    storySharePuzzleType={puzzleType}
                    storyId={storyPresentation.storyId ?? undefined}
                    showSlidingPuzzle={storyPresentation.showSlidingPuzzle}
                    showJigsawPuzzle={storyPresentation.showJigsawPuzzle}
                >
                    {playStoryDialogChildren}
                </StoryPlayStoryDialog>
            ) : null}

            {/* Subscription Dialog */}
            <SubscriptionDialog
                open={subscriptionModalOpen}
                onClose={() => setSubscriptionModalOpen(false)}
                onSubscribe={handleSubscribe}
                triggerReason={subscriptionTriggerReason}
                userSubscription={userSubscription}
            />

            {/* Login Confirmation Dialog */}
            <LoginConfirmDialog
                open={loginDialogOpen}
                onClose={() => setLoginDialogOpen(false)}
                onConfirm={() => {
                    navigate('/login');
                }}
                title={t("puzzle.playCross.login-required")}
                message={
                    loginDialogFeature === 'hint'
                        ? t("puzzle.playCross.login-hint-msg")
                        : t("puzzle.playCross.login-pdf-msg")
                }
            />

            {/* Welcome-back popup disabled — timer still pauses while tab is hidden
            <GamePausedOverlay
                open={pauseOverlayOpen}
                onContinue={() => {
                    continuePlaying();
                    setTimerPaused(false);
                }}
            />
            */}

            {/* PDF format selection dialog */}
            <PdfFormatDialog
                open={pdfFormatDialogOpen}
                onClose={() => setPdfFormatDialogOpen(false)}
                onConfirm={handlePdfFormatConfirm}
            />

            {/* Feedback Dialog */}
            <FeedbackDialog
                open={feedbackDialogOpen}
                onClose={() => setFeedbackDialogOpen(false)}
                onSubmit={async (feedbackData) => {
                    try {
                        await FeedbackService.submitFeedback({
                            gridId: null,
                            crossId: crossTplId,
                            cloudUserId: userInfo?.cloudUser?.id || null,
                            stars: feedbackData.stars,
                            difficultyDelta: feedbackData.difficultyDelta,
                            comment: feedbackData.comment
                        });
                        setSnackbarMessage(t("puzzle.playCross.feedback-thanks"));
                        setSnackbarSeverity("success");
                        setSnackbarOpen(true);
                    } catch (error) {
                        Logger.error("Error submitting feedback:", error);
                        setSnackbarMessage(t("puzzle.playCross.feedback-error"));
                        setSnackbarSeverity("error");
                        setSnackbarOpen(true);
                    }
                }}
                puzzleType={puzzleType}
            />

            {/* Non-blocking word-reveal celebration */}
            {wordRevealFx ? (
                <WordRevealFx
                    fxId={wordRevealFx.fxId}
                    word={wordRevealFx.word}
                    cellRects={wordRevealFx.cellRects}
                    bankRect={wordRevealFx.bankRect}
                    onComplete={handleWordRevealComplete}
                    onBankImpact={handleWordRevealBankImpact}
                />
            ) : null}

            {/* Audio element for applause sound */}
            <audio
                ref={audioRef}
                preload="auto"
                src={require("../../../assets/sounds/applause-sound-effect-240470.mp3")}
            />
        </MistPlayShell>
    );
};

export default CrossPlayMain;

